import React, { useEffect, useState, useCallback, useMemo } from "react";
import Navbar from "./Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import UserAvatar from './components/UserAvatar';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Table, Badge, Dropdown, Modal, Form, Spinner, Accordion, FloatingLabel, Pagination, ButtonGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import Tab from 'react-bootstrap/Tab';
import Nav from 'react-bootstrap/Nav';
import { toast } from 'react-toastify';
import axios from "axios";
import { useNotifications } from "./contexts/NotificationsContext";
import { API_ENDPOINTS } from './config/api';
import ProgramPGYUtils from './utils/programPGYUtils';
import PageTitle from './components/PageTitle';

import {
  buildPrintDocument,
  openPrintWindow,
  buildLeaveAllotmentChart,
  buildChartItem
} from './utils/printUtility';

const DirectorWorkLogDashboard = () => {
  const [residents, setResidents] = useState([]);
  const [pendingResidents, setPendingResidents] = useState([]);
  const [incompleteResidents, setIncompleteResidents] = useState([]);
  const [month, setMonth] = useState(moment().month());
  const [year, setYear] = useState(moment().year());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navigating, setNavigating] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportResident, setReportResident] = useState(null);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [unlockResident, setUnlockResident] = useState(null);
  const [activeTab, setActiveTab] = useState('work');
  const [workLogActiveTab, setWorkLogActiveTab] = useState('complete');
  const [vacationRequests, setVacationRequests] = useState([]);
  const [leaveAllotments, setLeaveAllotments] = useState([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [sortAsc, setSortAsc] = useState(true);
  const [detailModal, setDetailModal] = useState({ show: false, request: null });
  const [search, setSearch] = useState("");
  const [filterPGY, setFilterPGY] = useState('All');
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);
  const [filterAcademicYear, setFilterAcademicYear] = useState(() => {
    const currentMonth = moment().month();
    const currentYear = moment().year();
    return currentMonth >= 6 ? currentYear : currentYear - 1;
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState({ vacationDays: 20, sickDays: 10, academicYearStartMonth: 6, academicYearStartDay: 1 });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  // Controls when leave settings should start taking effect.
  // "current" => update current academic year allotments (July 1 of the current academic year).
  // "next" => update next academic year allotments (July 1 of the next academic year).
  const [settingsEffectiveAcademicYear, setSettingsEffectiveAcademicYear] = useState('next');
  const [rotationSchedules, setRotationSchedules] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [currentViolationPage, setCurrentViolationPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentAllotmentPage, setCurrentAllotmentPage] = useState(1);
  const [allotmentItemsPerPage, setAllotmentItemsPerPage] = useState(10);
  const [weeklyHeatmapOffset, setWeeklyHeatmapOffset] = useState(0);
  const [residentWeeklyLogs, setResidentWeeklyLogs] = useState({});
  const [loadingWeeklyHeatmap, setLoadingWeeklyHeatmap] = useState(false);
  const [canLoadHeatmap, setCanLoadHeatmap] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(() => {
    const storedPreference = localStorage.getItem("directorWorkLogShowHeatmap");
    return storedPreference !== "false";
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [selectedResidentAllotment, setSelectedResidentAllotment] = useState(null);
  const [residentVacationRequests, setResidentVacationRequests] = useState([]);
  const [loadingVacationRequests, setLoadingVacationRequests] = useState(false);
  const [unlockRequests, setUnlockRequests] = useState([]);
  const [loadingUnlockRequests, setLoadingUnlockRequests] = useState(false);
  const [showUnlockRequestModal, setShowUnlockRequestModal] = useState(false);
  const [selectedUnlockRequest, setSelectedUnlockRequest] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { addWorkLogNotification, addLeaveRequestNotification } = useNotifications();

  // Utility: parse hash string to object
  const parseHash = useCallback(() => {
    const hash = (location.hash || window.location.hash).replace(/^#/, '');
    const params = new URLSearchParams(hash);
    return {
      tab: params.get('tab') || 'work',
      month: params.get('month') ? parseInt(params.get('month')) : moment().month(),
      year: params.get('year') ? parseInt(params.get('year')) : moment().year(),
    };
  }, [location.hash]);

  // On mount and when hash changes: set state from hash
  useEffect(() => {
    const { tab, month: hashMonth, year: hashYear } = parseHash();
    setActiveTab(tab);
    setMonth(hashMonth);
    setYear(hashYear);
  }, [location.hash, parseHash]);

  // Check for recent pending leave requests (for immediate notification on tab load)
  const checkForRecentPendingRequests = (requests) => {
    console.log('Checking for recent pending leave requests on tab load...');
    
    requests.forEach(request => {
      if (request.status === 'Pending') {
        const requestTime = new Date(request.createdAt);
        const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
        
        console.log(`Checking pending request for ${request.resident?.firstName} ${request.resident?.lastName}`);
        console.log(`  Request time: ${requestTime}`);
        console.log(`  Thirty minutes ago: ${thirtyMinutesAgo}`);
        console.log(`  Is recent: ${requestTime > thirtyMinutesAgo}`);
        
        if (requestTime > thirtyMinutesAgo) {
          console.log(`  🎉 Adding immediate notification for pending request: ${request.resident?.firstName} ${request.resident?.lastName}`);
          addLeaveRequestNotification(request);
        }
      }
    });
  };

  const applyWorkLogSummaryWithViolations = useCallback(async (m, y) => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    const summaryUrl = `${API_ENDPOINTS.DIRECTOR}/work-logs-summary?month=${m}&year=${y}`;
    const violationsUrl = `${API_ENDPOINTS.DIRECTOR}/work-hour-violations?month=${m}&year=${y}`;
    const [res, violationsRes] = await Promise.all([
      fetch(summaryUrl, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(violationsUrl, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (!res.ok) return false;
    const data = await res.json();
    const violations = violationsRes.ok ? await violationsRes.json() : [];
    const violationsByResident = {};
    violations.forEach((violation) => {
      if (!violationsByResident[violation.ResidentId]) {
        violationsByResident[violation.ResidentId] = [];
      }
      violationsByResident[violation.ResidentId].push(violation);
    });
    data.forEach((resident) => {
      const residentViolations = violationsByResident[resident.id] || [];
      resident.hasViolation = residentViolations.length > 0;
      resident.violationWeeks = residentViolations.map((violation) => ({
        weekStart: moment(violation.weekStart).toDate(),
        weekEnd: moment(violation.weekEnd).toDate(),
        weekTotal: `${Math.floor(violation.totalMinutes / 60)}:${(violation.totalMinutes % 60).toString().padStart(2, "0")}`,
        hours: Math.floor(violation.totalMinutes / 60),
        minutes: violation.totalMinutes % 60,
        overLimit: violation.overLimitMinutes,
      }));
    });
    setResidents(data.filter((r) => r.status === "complete"));
    setPendingResidents(data.filter((r) => r.status === "pending"));
    setIncompleteResidents(data.filter((r) => r.status === "incomplete"));
    return true;
  }, []);

  // Whenever month/year changes, fetch residents
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const ok = await applyWorkLogSummaryWithViolations(month, year);
        if (!ok) setError("Failed to fetch resident data");
      } catch {
        setError("An error occurred while fetching resident data");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [month, year, applyWorkLogSummaryWithViolations]);

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  // Fetch program details on component mount
  useEffect(() => {
    fetchProgramDetails();
  }, []);

  // Fetch vacation requests
  useEffect(() => {
    if (activeTab === 'vacation') {
      const fetchRequests = async () => {
        try {
          const token = localStorage.getItem('token');
                const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
          if (res.ok) {
            const data = await res.json();
            setVacationRequests(data);
          }
        } catch (err) {
          // ignore
        }
      };
      fetchRequests();
    }
  }, [activeTab, showApproveModal, showDenyModal]);

  // Fetch unlock requests
  useEffect(() => {
    if (activeTab === 'work') {
      const fetchUnlockRequests = async () => {
        setLoadingUnlockRequests(true);
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-requests`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUnlockRequests(data);
          }
        } catch (err) {
          console.error('Error fetching unlock requests:', err);
        } finally {
          setLoadingUnlockRequests(false);
        }
      };
      fetchUnlockRequests();
    }
  }, [activeTab]);

  // Add this new useEffect for fetching leave allotments
  useEffect(() => {
    if (activeTab === 'leave') {
      const fetchLeaveAllotments = async () => {
        try {
          console.log('Fetching leave allotments...');
          const token = localStorage.getItem('token');
                const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/leave-allotments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
          if (res.ok) {
            const data = await res.json();
            console.log('Received leave allotments:', data);
            setLeaveAllotments(data);
          } else {
            console.error('Failed to fetch leave allotments:', res.status);
            const errorData = await res.json();
            console.error('Error details:', errorData);
          }
        } catch (err) {
          console.error('Error fetching leave allotments:', err);
        }
      };
      fetchLeaveAllotments();
    }
  }, [activeTab]);

  // Rotation schedules are program-scoped (not month-specific); fetch once per mount
  useEffect(() => {
    const fetchRotationSchedules = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API_ENDPOINTS.SCHEDULES}/rotations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRotationSchedules(res.data);
      } catch (err) {
        // ignore
      }
    };
    fetchRotationSchedules();
  }, []);

  const heatmapResidents = useMemo(
    () =>
      [...residents, ...pendingResidents, ...incompleteResidents]
        .filter((resident, index, arr) => arr.findIndex((item) => item.id === resident.id) === index)
        .filter((resident) => {
          if (!search.trim()) return true;
          const s = search.toLowerCase();
          return `${resident.firstName} ${resident.lastName}`.toLowerCase().includes(s);
        })
        .filter((resident) => filterPGY === "All" || String(resident.year) === filterPGY),
    [residents, pendingResidents, incompleteResidents, search, filterPGY]
  );

  const handleViewClick = (residentId) => {
    setNavigating(true);
    try {
      navigate(`/director/work-log/${residentId}/${month}/${year}?from=${encodeURIComponent(window.location.hash)}`);
    } catch (err) {
      setError("Failed to navigate to resident details");
      console.error("Navigation error:", err);
    } finally {
      setNavigating(false);
    }
  };

  const handleMonthChange = (offset) => {
    const newDate = moment().month(month).year(year).add(offset, 'months');
    setMonth(newDate.month());
    setYear(newDate.year());
  };

  const handleOpenReportModal = (resident) => {
    setReportResident(resident);
    setReportStartDate("");
    setReportEndDate("");
    setShowReportModal(true);
  };

  const handleCloseReportModal = () => {
    setShowReportModal(false);
    setReportResident(null);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportResident || !reportStartDate || !reportEndDate) return;
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("No authentication token found");
        return;
      }

      // Fetch the work log data for the date range
      const res = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/work-log-detail-range?residentId=${reportResident.id}&startDate=${reportStartDate}&endDate=${reportEndDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch data: ${res.status}`);
      }

      const data = await res.json();
      if (!data.resident) {
        throw new Error("Resident not found");
      }

      const resident = data.resident;
      const weeks = data.weeks || [];
      const residentName = `${resident.firstName} ${resident.lastName}`;
      const dateRange = `${moment(reportStartDate).format("MMMM D, YYYY")} - ${moment(reportEndDate).format("MMMM D, YYYY")}`;
      const currentDate = moment().format('MMMM DD, YYYY hh:mm A');
      const logoUrl = `${window.location.origin}/images/logo3.svg`;

      // Build table HTML
      let tableContent = '';
      if (weeks.length === 0) {
        tableContent = '<div class="text-center p-5 text-secondary">No work logs found for this period</div>';
      } else {
        // Group weeks by month and build rows with month headers
        // First, group weeks by month
        const weeksByMonth = {};
        weeks.forEach(week => {
          const weekStart = moment(week.weekStart);
          const monthKey = weekStart.format('YYYY-MM');
          if (!weeksByMonth[monthKey]) {
            weeksByMonth[monthKey] = [];
          }
          weeksByMonth[monthKey].push(week);
        });
        
        // Helper function to calculate month total
        const calculateMonthTotal = (monthWeeks) => {
          const totalMinutes = monthWeeks.reduce((total, week) => {
            const weekTotal = week.weekTotal.split(":");
            return total + (parseInt(weekTotal[0]) * 60 + parseInt(weekTotal[1]));
          }, 0);
          const totalHours = Math.floor(totalMinutes / 60);
          const totalMins = (totalMinutes % 60).toString().padStart(2, "0");
          return `${totalHours}:${totalMins}`;
        };
        
        let tableRows = '';
        const sortedMonths = Object.keys(weeksByMonth).sort();
        
        sortedMonths.forEach((monthKey, monthIdx) => {
          const monthWeeks = weeksByMonth[monthKey];
          const monthName = moment(monthWeeks[0].weekStart).format('MMMM YYYY');
          const monthTotal = calculateMonthTotal(monthWeeks);
          
          // Add month header
          tableRows += `
            <tr style="background-color: #eaedf2; border-bottom: 1px solid #d4e0ed;">
              <td colspan="8" style="padding: 12px 20px; font-weight: bold; font-size: 14px; text-align: left; color: #252F4A;">
                ${monthName}
              </td>
              <td style="padding: 12px 20px; font-weight: bold; font-size: 14px; text-align: left; color: #252F4A; border-left: 1px solid #d4e0ed;">
                ${monthTotal}<span style="font-size: 12px; color: #666;"> Hrs for ${monthName}</span>
              </td>
            </tr>
          `;
          
          // Add week rows for this month
          monthWeeks.forEach((week, wIdx) => {
            const weekStart = moment(week.weekStart);
            const weekTotalMins = (() => {
              const [h, m] = week.weekTotal.split(":").map(Number);
              return h * 60 + m;
            })();
            const isViolation = weekTotalMins > 4800;
            
            const dayCells = week.days.map((day, dIdx) => {
              if (day && day.leaveType === 'Sick') {
                return '<td style="text-align: center; padding: 10px;"><span class="badge badge-warning">Sick Leave</span><br><span style="font-size: 11px; color: #666;">0h 00m</span></td>';
              } else if (day && day.leaveType === 'Vacation') {
                return '<td style="text-align: center; padding: 10px;"><span class="badge badge-info">Vacation Leave</span><br><span style="font-size: 11px; color: #666;">0h 00m</span></td>';
              } else if (day && day.total) {
                const [hours, minutes] = day.total.split(":");
                return `<td style="text-align: center; padding: 10px;"><span style="font-weight: 600; font-size: 14px;">${day.total}</span><br><span style="font-size: 11px; color: #666;">${hours}h ${minutes}m</span></td>`;
              } else {
                return '<td style="text-align: center; padding: 10px;"></td>';
              }
            }).join('');
            
            const violationBadge = isViolation 
              ? '<span class="badge badge-danger" style="margin-left: 8px;">⚠ Violation</span>'
              : '<span class="badge badge-success" style="margin-left: 8px;">✓ Compliant</span>';
            
            const weekEnd = moment(week.weekStart).add(6, "days");
            const weekNumber = weekStart.format("w");
            const weekRange = `${weekStart.format("MMM D")} - ${weekEnd.format("MMM D, YYYY")}`;
            
            tableRows += `
              <tr>
                <td style="text-align: left; padding: 10px; font-weight: 600;">
                  <div>Week ${weekNumber}</div>
                  <div style="font-size: 11px; color: #666; margin-top: 4px;">${weekRange}</div>
                </td>
                ${dayCells}
                <td style="text-align: center; padding: 10px; border-left: 1px solid #ddd;">
                  <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                    <span style="font-weight: bold; font-size: 14px;">${week.weekTotal}</span>
                    <span style="font-size: 12px; color: #666;">/ 80 Hrs</span>
                    ${violationBadge}
                  </div>
                </td>
              </tr>
            `;
          });
        });
        
        // Calculate total
        const totalMinutes = weeks.reduce((total, week) => {
          const weekTotal = week.weekTotal.split(":");
          return total + (parseInt(weekTotal[0]) * 60 + parseInt(weekTotal[1]));
        }, 0);
        const totalHours = Math.floor(totalMinutes / 60);
        const totalMins = (totalMinutes % 60).toString().padStart(2, "0");
        const totalTime = `${totalHours}:${totalMins}`;
        
        tableContent = `
          <table>
            <thead>
              <tr>
                <th>Week</th>
                <th>Sun</th>
                <th>Mon</th>
                <th>Tue</th>
                <th>Wed</th>
                <th>Thu</th>
                <th>Fri</th>
                <th>Sat</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
              <tr style="font-weight: 600; background-color: #f8f9fb;">
                <td colspan="8" style="text-align: right; padding: 15px; font-size: 14px;">Total for Range:</td>
                <td style="padding: 15px; font-size: 16px; font-weight: bold; border-left: 1px solid #ddd;">${totalTime}</td>
              </tr>
            </tbody>
          </table>
        `;
      }

      const customStyles = `
        @page {
          size: landscape;
          margin: 1cm;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        
        thead {
          background: var(--gray-100);
          color: var(--gray-800);
          border-bottom: 2px solid var(--border-bg);
        }
        
        th {
          padding: 12px;
          text-align: center;
          font-weight: 600;
        }
        
        th:first-child {
          text-align: left;
          padding-left: 20px;
        }
        
        tbody tr {
          border-bottom: 1px solid var(--border-bg);
        }
        
        tbody tr:nth-child(even) {
          background: var(--gray-100);
        }
        
        td {
          padding: 10px;
          text-align: center;
          color: var(--gray-700);
        }
        
        td:first-child {
          text-align: left;
          font-weight: 600;
          color: var(--gray-800);
        }
        
        .badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: bold;
          display: inline-block;
        }
        
        .badge-warning {
          background-color: var(--yellow);
          color: #000;
        }
        
        .badge-info {
          background-color: var(--indigo);
          color: white;
        }
        
        .badge-success {
          background-color: var(--green);
          color: white;
        }
        
        .badge-danger {
          background-color: var(--red);
          color: white;
        }
      `;

      const tableSection = `
        <div class="print-section">
          <h2>Work Log Report - ${dateRange}</h2>
          ${tableContent}
        </div>
      `;

      const printContent = buildPrintDocument({
        title: `Work Log Report - ${residentName}`,
        content: tableSection,
        headerTitle: 'Resident Work Log Report',
        headerSubtitle: dateRange,
        logoUrl,
        showPrintButton: true,
        additionalHeaderInfo: [
          { label: 'Resident', value: residentName },
          { label: 'Generated', value: currentDate }
        ],
        footerMessage: 'This is a system-generated report. For questions, please contact the program director.',
        customStyles,
        pageSize: 'landscape'
      });

      openPrintWindow(printContent);
      setShowReportModal(false);
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error(err.message || 'Failed to generate report');
    }
  };

  const handleOpenUnlockConfirm = (resident) => {
    setUnlockResident(resident);
    setShowUnlockConfirm(true);
  };

  const handleCloseUnlockConfirm = () => {
    setShowUnlockConfirm(false);
    setUnlockResident(null);
  };

  const handleUnlockSubmission = async () => {
    if (!unlockResident) return;
    setShowUnlockConfirm(false);
    try {
      const token = localStorage.getItem('token');
      // Find the most recent submitted (locked) submission
      const submittedSubmission = unlockResident.WorkHourSubmissions?.find(s => s.submitted);
      if (!submittedSubmission) {
        toast.error('No submitted work hour logs found to unlock.');
        return;
      }
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-work-hour-submission/${submittedSubmission.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Submission unlocked. Resident can now edit work hours.');
        // Refresh the data to show updated status
        window.location.reload();
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to unlock submission.');
      }
    } catch (err) {
      toast.error('Error unlocking submission.');
    }
  };

  const handleApproveUnlockRequest = async (request) => {
    try {
      const token = localStorage.getItem('token');
      
      // First, try to unlock the submission directly using the existing endpoint
      // This ensures we use the exact same code path that works
      if (request.submissionId) {
        const unlockRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-work-hour-submission/${request.submissionId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (unlockRes.ok) {
          // Mark the notification as read
          await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-requests/${request.notificationId}/deny`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          });
          
          toast.success('Unlock request approved. Submission unlocked.');
          
          // Remove the request from the list
          setUnlockRequests(prev => prev.filter(r => r.id !== request.id));
          
          const [, refreshRes] = await Promise.all([
            applyWorkLogSummaryWithViolations(month, year),
            fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-requests`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);
          if (refreshRes.ok) {
            setUnlockRequests(await refreshRes.json());
          }
        } else {
          const unlockData = await unlockRes.json();
          toast.error(unlockData.message || 'Failed to unlock submission.');
        }
      } else {
        // Fallback to the approve endpoint if no submissionId
        const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-requests/${request.notificationId}/approve`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (res.ok) {
          toast.success('Unlock request approved. Submission unlocked.');
          setUnlockRequests(prev => prev.filter(r => r.id !== request.id));
          
          await applyWorkLogSummaryWithViolations(month, year);
        } else {
          const data = await res.json();
          toast.error(data.message || 'Failed to approve unlock request.');
        }
      }
    } catch (err) {
      console.error('Error approving unlock request:', err);
      toast.error('Error approving unlock request.');
    }
  };

  const handleDenyUnlockRequest = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-requests/${request.notificationId}/deny`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Unlock request denied.');
        // Remove the request from the list
        setUnlockRequests(prev => prev.filter(r => r.id !== request.id));
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to deny unlock request.');
      }
    } catch (err) {
      toast.error('Error denying unlock request.');
    }
  };

  // Helper function to calculate workdays and weekends
  const calculateWorkdaysAndWeekends = (startDate, endDate) => {
    let workdays = 0;
    let weekends = 0;
    const current = moment(startDate);
    const end = moment(endDate);
    
    while (current.isSameOrBefore(end, 'day')) {
      const dayOfWeek = current.day(); // 0 = Sunday, 6 = Saturday
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        weekends++;
      } else {
        workdays++;
      }
      current.add(1, 'day');
    }
    
    const parts = [];
    if (workdays > 0) {
      parts.push(`${workdays}Days,`);
    }
    if (weekends > 0) {
      parts.push(`${weekends}Wnd`);
    }
    
    return parts.length > 0 ? parts.join(' ') : '0Days';
  };

  // Filter and sort vacation requests
  const filteredRequests = vacationRequests
    .filter(r => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      const resident = r.resident ? `${r.resident.firstName} ${r.resident.lastName}`.toLowerCase() : "";
      const reason = (r.reason || "").toLowerCase();
      const dateRange = `${moment(r.startDate).format('YYYY-MM-DD')} to ${moment(r.endDate).format('YYYY-MM-DD')}`;
      return (
        resident.includes(s) ||
        reason.includes(s) ||
        dateRange.includes(s)
      );
    })
    .filter(r => (filterPGY === 'All' || (r.resident && String(r.resident.year) === filterPGY)))
    .filter(r => (filterStatus === 'All' || r.status === filterStatus))
    .filter(r => (filterType === 'All' || r.type === filterType))
    .sort((a, b) => sortAsc
      ? new Date(a.createdAt) - new Date(b.createdAt)
      : new Date(b.createdAt) - new Date(a.createdAt)
    );

  // Separate requests into new (Pending) and previous (Approved/Denied)
  const newRequests = filteredRequests.filter(r => r.status === 'Pending');
  const previousRequests = filteredRequests.filter(r => r.status !== 'Pending');

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests/${selectedRequest.id}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Request approved successfully');
        setShowApproveModal(false);
        setSelectedRequest(null);
        // Refresh the requests list
        const updatedRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (updatedRes.ok) {
          const data = await updatedRes.json();
          setVacationRequests(data);
        }
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to approve request');
      }
    } catch (err) {
      toast.error('Error approving request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDenyRequest = async () => {
    if (!selectedRequest || !denyReason) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests/${selectedRequest.id}/deny`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: denyReason })
      });
      if (res.ok) {
        toast.success('Request denied successfully');
        setShowDenyModal(false);
        setSelectedRequest(null);
        setDenyReason('');
        // Refresh the requests list
        const updatedRes = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (updatedRes.ok) {
          const data = await updatedRes.json();
          setVacationRequests(data);
        }
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to deny request');
      }
    } catch (err) {
      toast.error('Error denying request');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/leave-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch {}
    setSettingsLoading(false);
  };

  const openSettingsModal = () => {
    fetchSettings();
    // Preserve the previous behavior by defaulting to "next" (upcoming academic year).
    setSettingsEffectiveAcademicYear('next');
    setShowSettingsModal(true);
  };

  const closeSettingsModal = () => setShowSettingsModal(false);

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettings(s => ({ ...s, [name]: Number(value) }));
  };

  const handleSettingsSave = async () => {
    setSettingsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const now = moment();
      const currentAcademicYearStart = now.month() >= 6 ? now.year() : now.year() - 1;
      const applyFromAcademicYearStart =
        settingsEffectiveAcademicYear === 'current'
          ? currentAcademicYearStart
          : currentAcademicYearStart + 1;

      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/leave-settings`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...settings, applyFromAcademicYearStart })
      });
      if (res.ok) {
        toast.success('Settings updated');
        setShowSettingsModal(false);
      } else {
        const data = await res.json();
        toast.error(data.message || 'Failed to update settings');
      }
    } catch {
      toast.error('Failed to update settings');
    }
    setSettingsSaving(false);
  };

  // Helper to get rotation for a resident for the current month
  const getResidentRotation = (resident) => {
    if (!rotationSchedules || !resident) return "N/A";
    const pgy = String(resident.year);
    const scheduleData = rotationSchedules[pgy]?.schedule;
    if (!scheduleData) {
      console.log("No scheduleData for PGY", pgy, rotationSchedules);
      return "N/A";
    }
    const residentData = scheduleData[resident.id];
    if (!residentData) {
      console.log("No residentData for resident.id", resident.id, scheduleData);
      return "N/A";
    }
    const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
    console.log("Looking for rotation:", {pgy, residentId: resident.id, monthStr, residentData});
    return residentData.rotations?.[monthStr] || "N/A";
  };

  // Pagination helpers
  const getFilteredResidents = () => {
    return residents
      .filter(r => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
      })
      .filter(r => filterPGY === 'All' || String(r.year) === filterPGY);
  };

  const getFilteredViolations = () => {
    return residents
      .filter(r => r.hasViolation)
      .filter(r => {
        if (!search.trim()) return true;
        const s = search.toLowerCase();
        return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
      })
      .filter(r => filterPGY === 'All' || String(r.year) === filterPGY);
  };

  const getPaginatedData = (data, page, setPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const completeFilteredResidents = getFilteredResidents();
  const completePageResidents = getPaginatedData(completeFilteredResidents, currentPage, setCurrentPage);

  const parseTimeToHours = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return null;
    const [hoursPart, minsPart] = timeStr.split(":");
    const hours = Number(hoursPart);
    const mins = Number(minsPart);
    if (Number.isNaN(hours) || Number.isNaN(mins)) return null;
    return hours + (mins / 60);
  };

  const getResidentInitials = (resident) => {
    const first = resident?.firstName?.[0] || "";
    const last = resident?.lastName?.[0] || "";
    return `${first}${last}`.toUpperCase() || "NA";
  };

  const getHeatColor = (hours) => {
    if (hours === null) return "var(--gray-200)"; // No recording
    if (hours <= 8) return "var(--green)"; // Green
    if (hours <= 10) return "var(--yellow)"; // Yellow
    return "var(--red)"; // Red
  };

  const getHeatTextColor = (hours) => {
    if (hours === null || hours <= 8) return "#ffffff";
    if (hours <= 10) return "#212529";
    return "#ffffff";
  };

  const heatmapDayConfig = [
    { label: "S", fullLabel: "Sunday", sourceIndex: 6 },
    { label: "M", fullLabel: "Monday", sourceIndex: 0 },
    { label: "T", fullLabel: "Tuesday", sourceIndex: 1 },
    { label: "W", fullLabel: "Wednesday", sourceIndex: 2 },
    { label: "T", fullLabel: "Thursday", sourceIndex: 3 },
    { label: "F", fullLabel: "Friday", sourceIndex: 4 },
    { label: "S", fullLabel: "Saturday", sourceIndex: 5 }
  ];

  const getWeekStartsForHeatmap = () => {
    const weekStartSet = new Set();
    heatmapResidents.forEach((resident) => {
      const weeks = residentWeeklyLogs[resident.id] || [];
      weeks.forEach((week) => {
        if (week?.weekStart) {
          weekStartSet.add(moment.utc(week.weekStart).startOf("week").format("YYYY-MM-DD"));
        }
      });
    });

    // Always include month week buckets so residents with no logs still render in gray.
    // Include one extra week on both sides to cover boundary navigation (e.g. Mar 1 - Mar 7 from Feb view).
    const monthStart = moment({ year, month, date: 1 }).startOf("month").startOf("week").subtract(1, "week");
    const monthEnd = moment({ year, month, date: 1 }).endOf("month").endOf("week").add(1, "week");
    const cursor = monthStart.clone();
    while (cursor.isSameOrBefore(monthEnd, "day")) {
      weekStartSet.add(cursor.format("YYYY-MM-DD"));
      cursor.add(7, "days");
    }

    return Array.from(weekStartSet)
      .map((dateStr) => moment.utc(dateStr, "YYYY-MM-DD"))
      .sort((a, b) => a.valueOf() - b.valueOf());
  };

  const getSelectedWeekMoment = () => {
    const weekStarts = getWeekStartsForHeatmap();
    if (weekStarts.length === 0) return moment().startOf("week");
    const safeIndex = Math.max(0, Math.min(weeklyHeatmapOffset, weekStarts.length - 1));
    return weekStarts[safeIndex];
  };

  const getResidentWeekForSelectedWeek = (residentId, selectedWeekStart) => {
    const weeks = residentWeeklyLogs[residentId] || [];
    if (!selectedWeekStart) return null;
    return weeks.find((week) => moment.utc(week.weekStart).startOf("week").isSame(selectedWeekStart, "day")) || null;
  };

  const getResidentDayHours = (residentId, sourceDayIndex, selectedWeekStart) => {
    const selectedWeek = getResidentWeekForSelectedWeek(residentId, selectedWeekStart);
    if (!selectedWeek || !Array.isArray(selectedWeek.days)) return { hours: null, display: "No recording", weekTotal: "0:00" };
    const day = selectedWeek.days[sourceDayIndex];
    if (!day?.total) return { hours: null, display: "No recording", weekTotal: selectedWeek.weekTotal || "0:00" };
    const hours = parseTimeToHours(day.total);
    return {
      hours,
      display: day.total,
      weekTotal: selectedWeek.weekTotal || "0:00"
    };
  };

  const totalPages = (data) => Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (newPage, setPage) => {
    setPage(newPage);
  };

  // Allotment pagination logic
  const filteredAllotments = leaveAllotments
    .filter(allotment => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      const resident = allotment.resident
        ? `${allotment.resident.firstName} ${allotment.resident.lastName}`.toLowerCase()
        : "";
      return resident.includes(s);
    })
    .filter(allotment =>
      filterPGY === 'All' ||
      (allotment.resident && String(allotment.resident.year) === filterPGY)
    )
    .filter(allotment => {
      const allotmentYear = moment(allotment.yearStart).year();
      return allotmentYear === filterAcademicYear;
    });

  const totalLeaveResidents = filteredAllotments.length;

  const averageVacationUsedDays = totalLeaveResidents > 0
    ? filteredAllotments.reduce(
        (sum, allotment) => sum + (allotment.usedVacationDays || 0),
        0
      ) / totalLeaveResidents
    : 0;

  const LOW_BALANCE_THRESHOLD = 5;

  const lowBalanceResidentsCount = filteredAllotments.filter(allotment => {
    const total = allotment.vacationDays || 0;
    const used = allotment.usedVacationDays || 0;
    const remaining = Math.max(total - used, 0);
    return remaining <= LOW_BALANCE_THRESHOLD;
  }).length;

  const goodStandingResidentsCount = Math.max(
    totalLeaveResidents - lowBalanceResidentsCount,
    0
  );

  const pendingRequestsByResident = vacationRequests.reduce((acc, request) => {
    if (request.status === 'Pending' && request.residentId) {
      acc[request.residentId] = (acc[request.residentId] || 0) + 1;
    }
    return acc;
  }, {});

  const totalPendingLeaveRequests = Object.values(pendingRequestsByResident).reduce(
    (sum, count) => sum + count,
    0
  );

  const allotmentTotalPages = Math.ceil(totalLeaveResidents / allotmentItemsPerPage);
  const allotmentStartIndex = (currentAllotmentPage - 1) * allotmentItemsPerPage;
  const paginatedAllotments = filteredAllotments.slice(
    allotmentStartIndex,
    allotmentStartIndex + allotmentItemsPerPage
  );

  const handleAllotmentItemsPerPageChange = (newItemsPerPage) => {
    setAllotmentItemsPerPage(newItemsPerPage);
    setCurrentAllotmentPage(1); // Reset to first page when changing items per page
  };

  const handleAllotmentPageChange = (pageNumber) => {
    setCurrentAllotmentPage(pageNumber);
  };

  useEffect(() => {
    setResidentWeeklyLogs({});
    setWeeklyHeatmapOffset(0);
    setCanLoadHeatmap(false);
  }, [month, year]);

  useEffect(() => {
    localStorage.setItem("directorWorkLogShowHeatmap", String(showHeatmap));
  }, [showHeatmap]);

  useEffect(() => {
    if (!showHeatmap) {
      setResidentWeeklyLogs({});
      setLoadingWeeklyHeatmap(false);
    }
  }, [showHeatmap]);

  // Defer heatmap loading so primary dashboard data renders first (only when heatmap is visible).
  useEffect(() => {
    if (!showHeatmap) {
      setCanLoadHeatmap(false);
      return;
    }
    if (loading) return;
    const timer = setTimeout(() => {
      setCanLoadHeatmap(true);
    }, 0);
    return () => clearTimeout(timer);
  }, [loading, month, year, showHeatmap]);

  useEffect(() => {
    if (!showHeatmap || !canLoadHeatmap || loading) return;

    const controller = new AbortController();

    const run = async () => {
      setLoadingWeeklyHeatmap(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setResidentWeeklyLogs({});
          return;
        }
        const res = await fetch(
          `${API_ENDPOINTS.DIRECTOR}/work-logs-heatmap?month=${month}&year=${year}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          }
        );
        if (!res.ok) {
          setResidentWeeklyLogs({});
          return;
        }
        const data = await res.json();
        setResidentWeeklyLogs(data.byUserId && typeof data.byUserId === "object" ? data.byUserId : {});
      } catch (e) {
        if (e.name !== "AbortError") {
          setResidentWeeklyLogs({});
        }
      } finally {
        setLoadingWeeklyHeatmap(false);
      }
    };

    run();
    return () => controller.abort();
  }, [month, year, showHeatmap, canLoadHeatmap, loading]);

  useEffect(() => {
    const weekStarts = getWeekStartsForHeatmap();
    if (weekStarts.length === 0) {
      setWeeklyHeatmapOffset(0);
      return;
    }

    const maxIndex = weekStarts.length - 1;
    if (weeklyHeatmapOffset > maxIndex) {
      setWeeklyHeatmapOffset(maxIndex);
      return;
    }

    if (weeklyHeatmapOffset === 0) {
      const isCurrentMonth = month === moment().month() && year === moment().year();
      if (isCurrentMonth) {
        const currentWeekIndex = weekStarts.findIndex((weekStart) =>
          moment.utc().isBetween(weekStart, weekStart.clone().add(6, "days"), "day", "[]")
        );
        if (currentWeekIndex >= 0) {
          setWeeklyHeatmapOffset(currentWeekIndex);
        }
      }
    }
  }, [month, year, residentWeeklyLogs, heatmapResidents, weeklyHeatmapOffset]);

  // Fetch vacation requests for a specific resident
  const fetchResidentVacationRequests = async (residentId) => {
    setLoadingVacationRequests(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error('No authentication token found');
        setResidentVacationRequests([]);
        return;
      }
      
      // Use configured API endpoint to work in both dev and production
      const res = await fetch(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        setResidentVacationRequests([]);
        return;
      }
      const responseData = await res.json();

      // Normalize API shape: handle both array and { requests: [...] }
      const allRequests = Array.isArray(responseData)
        ? responseData
        : (responseData && Array.isArray(responseData.requests))
          ? responseData.requests
          : [];

      // Compute academic year window [start, end] inclusive: default July 1 -> June 30
      const academicYearStart = moment({
        year: parseInt(filterAcademicYear, 10),
        month: 6, // July (0-based)
        day: 1
      }).startOf('day');
      const academicYearEnd = moment({
        year: parseInt(filterAcademicYear, 10) + 1,
        month: 5, // June
        day: 30
      }).endOf('day');

      // Filter by resident, approved, and overlap with academic year window
      const approvedRequests = allRequests.filter((request) => {
        if (request.residentId !== residentId || request.status !== 'Approved') {
          return false;
        }
        const reqStart = moment(request.startDate).startOf('day');
        const reqEnd = moment(request.endDate || request.startDate).endOf('day');
        // Overlap if start <= AY_end && end >= AY_start
        const overlapsAcademicYear = reqStart.isSameOrBefore(academicYearEnd) && reqEnd.isSameOrAfter(academicYearStart);
        return overlapsAcademicYear;
      });

      console.log('Approved requests for resident (AY overlap):', approvedRequests.length);
      setResidentVacationRequests(approvedRequests);
    } catch (error) {
      console.error('Error fetching vacation requests:', error);
      setResidentVacationRequests([]);
    } finally {
      setLoadingVacationRequests(false);
    }
  };

  // Show vacation history modal
  const showVacationHistory = async (residentId, residentName, allotment = null) => {
    setSelectedResident({ id: residentId, name: residentName });
    setSelectedResidentAllotment(allotment);
    setShowHistoryModal(true);
    await fetchResidentVacationRequests(residentId);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedResident(null);
    setSelectedResidentAllotment(null);
    setResidentVacationRequests([]);
  };

  // Print leave history summary
  const handlePrintLeaveHistory = () => {
    const residentName = selectedResident?.name || 'Resident';
    const allotment = selectedResidentAllotment;
    const currentDate = moment().format('MMMM DD, YYYY hh:mm A');
    const academicYear = `${filterAcademicYear}-${parseInt(filterAcademicYear) + 1}`;
    const logoUrl = `${window.location.origin}/images/asset11.svg`;
    
    // Build table rows
    let tableRows = '';
    if (residentVacationRequests.length === 0) {
      tableRows = '<tr><td colspan="6" style="text-align: center; padding: 20px;">No approved leave requests found for the current academic year.</td></tr>';
    } else {
      tableRows = residentVacationRequests.map((request) => {
        const daysRequested = moment(request.endDate).diff(moment(request.startDate), 'days') + 1;
        const type = request.type === 'Sick' ? 'Sick Leave' : request.type;
        const startDate = moment(request.startDate).format('MMM DD, YYYY');
        const endDate = moment(request.endDate).format('MMM DD, YYYY');
        const reason = request.reason || 'No reason provided';
        return `<tr>
          <td>${type}</td>
          <td>${startDate}</td>
          <td>${endDate}</td>
          <td>${daysRequested}</td>
          <td><span class="badge badge-success">${request.status}</span></td>
          <td>${reason}</td>
        </tr>`;
      }).join('');
    }
    
    // Build allotment section with charts
    let allotmentSection = '';
    if (allotment) {
      const vacationUsed = allotment.usedVacationDays || 0;
      const vacationTotal = allotment.vacationDays || 0;
      const vacationRemaining = vacationTotal - vacationUsed;
      
      const sickUsed = allotment.usedSickDays || 0;
      const sickTotal = allotment.sickDays || 0;
      const sickRemaining = sickTotal - sickUsed;
      
      const vacationChart = buildLeaveAllotmentChart({
        used: vacationUsed,
        total: vacationTotal,
        color: '#007bff'
      });
      
      const sickChart = buildLeaveAllotmentChart({
        used: sickUsed,
        total: sickTotal,
        color: '#6610f2'
      });
      
      const vacationChartItem = buildChartItem({
        title: 'Vacation Leave',
        svgContent: vacationChart,
        stats: [
          { label: 'Used', value: `${vacationUsed} days` },
          { label: 'Total', value: `${vacationTotal} days` },
          { label: 'Remaining', value: `${vacationRemaining} days` }
        ]
      });
      
      const sickChartItem = buildChartItem({
        title: 'Sick Leave',
        svgContent: sickChart,
        stats: [
          { label: 'Used', value: `${sickUsed} days` },
          { label: 'Total', value: `${sickTotal} days` },
          { label: 'Remaining', value: `${sickRemaining} days` }
        ]
      });
      
      allotmentSection = `
        <div class="print-section">
          <h2>Leave Allotment Summary</h2>
          <div class="charts-container">
            ${vacationChartItem}
            ${sickChartItem}
          </div>
        </div>
      `;
    }
    
    const totalRequestsText = residentVacationRequests.length > 0 
      ? `<p style="margin-top: 15px; font-size: 14px; color: #666;"><strong>Total Requests:</strong> ${residentVacationRequests.length}</p>`
      : '';
    
    const tableSection = `
      <div class="print-section">
        <h2>Approved Leave Requests</h2>
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Days</th>
              <th>Status</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        ${totalRequestsText}
      </div>
    `;
    
    const content = `${allotmentSection}${tableSection}`;
    
    const printContent = buildPrintDocument({
      title: `Leave History - ${residentName}`,
      content,
      headerTitle: 'Leave History Summary',
      headerSubtitle: '',
      logoUrl,
      showPrintButton: true,
      additionalHeaderInfo: [
        { label: 'Resident', value: residentName },
        { label: 'Academic Year', value: academicYear },
        { label: 'Generated', value: currentDate }
      ],
      footerMessage: 'This is a system-generated report. For questions, please contact the program director.'
    });
    
    openPrintWindow(printContent);
  };

  // Pagination Component
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        }
      }
      
      return pages;
    };

    return (
      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end align-items-center">
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            {getPageNumbers().map((page, index) => (
              <li key={index} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                {page === '...' ? (
                  <span className="page-link">...</span>
                ) : (
                  <button 
                    className="page-link" 
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Resident Work Hour Logs" />

        <div className="bg-white shadow-sm">
          <Container>
            <Row>
              <Col lg={3} md={4} sm={9} className="col-9 order-first d-flex mb-2 mb-lg-0 mb-md-2 mb-sm-2 py-lg-0 py-md-3 py-3 align-items-center justify-content-start">
                <h6 className="mb-0 fw-semibold">Resident Time Sheets</h6>
              </Col>
              <Col lg={8} md={7} sm={12} className="order-3 order-lg-2 order-md-2 order-sm-3 col-12 d-flex align-items-center justify-content-between">
                <Tab.Container
                  activeKey={activeTab}
                  onSelect={key => {
                    setActiveTab(key);
                    // Update the hash in the URL
                    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
                    params.set('tab', key);
                    window.location.hash = params.toString();
                  }}
                >
                  <Nav variant="tabs" style={{marginBottom: '-1px'}}>
                    <Nav.Item>
                      <Nav.Link eventKey="work">Work Hour Logs</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="vacation">Leave Request</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="leave">Leave Tracking</Nav.Link>
                    </Nav.Item>
                  </Nav>  
                </Tab.Container>
              </Col>
              <Col lg={1} md={1} sm={3} className="col-3 order-2 order-lg-2 order-md-2 order-sm-2 d-flex py-lg-0 py-md-2 py-2 align-items-center justify-content-end">
                <Button
                  variant="light"
                  size="sm"
                  className="ms-2 d-flex align-items-center"
                  onClick={() => setShowHeatmap((prev) => !prev)}
                  title={showHeatmap ? "Hide Heatmap" : "Show Heatmap"}
                >
                  <i className={`bi ${showHeatmap ? "bi-eye-slash" : "bi-eye"}`}></i>
                </Button>
                <Button variant="light" size="sm" className="ms-2 d-flex align-items-center" onClick={openSettingsModal} title="Settings">
                  <i className="bi bi-gear-fill"></i>
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading work log data...</div>
          </div>
        </Container>
      </div>
    );
  }
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <PageTitle title="Work Log Dashboard" description="Monitor resident work hours, leave requests, and compliance tracking" />
      <Navbar pageTitle="Resident Work Hour Logs" />

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col lg={3} md={4} sm={9} className="col-9 order-first d-flex py-lg-0 py-md-3 py-3 align-items-center justify-content-start">
            <h6 className="mb-0 fw-semibold">Resident Time Sheets</h6>
            </Col>
            <Col lg={8} md={7} sm={12} className="order-3 order-lg-2 order-md-2 order-sm-3 col-12 d-flex align-items-center justify-content-between">
            <Tab.Container
              activeKey={activeTab}
              onSelect={key => {
                setActiveTab(key);
                // Update the hash in the URL
                const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
                params.set('tab', key);
                window.location.hash = params.toString();
              }}
            >
                    <Nav variant="tabs" className="scrollable-tabs" style={{marginBottom: '-1px'}}>
                    <Nav.Item>
              <Nav.Link eventKey="work">Work Hour Logs</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="vacation">Leave Request</Nav.Link>
            </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="leave">Leave Tracking</Nav.Link>
            </Nav.Item>
        </Nav>  
        </Tab.Container>
        </Col>
        <Col lg={1} md={1} sm={3} className="col-3 order-2 order-lg-2 order-md-2 order-sm-2 d-flex align-items-center justify-content-end">
              {activeTab === 'work' && (
                <Button
                variant="light-solid"
                size="sm"
                className="ms-2 d-flex align-items-center"
                onClick={() => setShowHeatmap((prev) => !prev)}
                title={showHeatmap ? "Hide Heatmap" : "Show Heatmap"}
              >
                <i className={`bi ${showHeatmap ? "bi-eye-slash" : "bi-eye"}`}></i>
              </Button>
              )}
              <Button variant="light" size="sm" className="ms-2 d-flex align-items-center" onClick={openSettingsModal} title="Settings">
                <i className="bi bi-gear-fill"></i>
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
          <Tab.Content>
            <Tab.Pane eventKey="work" active={activeTab === 'work'}>
            <Row>
              <Col lg={2} md={3} sm={12} className="mb-4">
                <div className="d-none d-lg-block d-md-block custom-sticky-top" style={{ top: '5rem' }}>
                  <Card className="shadow-sm border-0 ">
                    <Card.Header className="border-0 p-3" style={{ background: 'var(--bs-card-bg)' }}>
                      <h6 className="mb-0 fw-semibold">Menu</h6>
                    </Card.Header>
                    <Card.Body className="p-0 mb-2">
                      <Nav variant="pills" className="flex-column" activeKey={workLogActiveTab} onSelect={setWorkLogActiveTab}>
                        <Nav.Item>
                          <Nav.Link 
                            eventKey="complete" 
                            className="d-flex align-items-center justify-content-between py-25"
                          >
                            <span>Complete</span>
                            <Badge bg="success">{residents.length}</Badge>
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link 
                            eventKey="pending" 
                            className="d-flex align-items-center justify-content-between py-25"
                          >
                            <span>Pending</span>
                            <Badge bg="warning">{pendingResidents.length}</Badge>
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link 
                            eventKey="incomplete" 
                            className="d-flex align-items-center justify-content-between py-25"
                          >
                            <span>Incomplete</span>
                            <Badge bg="danger">{incompleteResidents.length}</Badge>
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </Card.Body>
                  </Card>
                </div>
                <div className="d-lg-none d-md-none">
                  <Accordion className="shadow-sm">
                    <Accordion.Item eventKey="0" className="border-0">
                      <Accordion.Header>Submission Status</Accordion.Header>
                      <Accordion.Body>
                            <div className="d-flex flex-column gap-2">
                              <Button
                            variant={workLogActiveTab === 'complete' ? 'success' : 'light'}
                                size="sm"
                                className="text-start d-flex align-items-center justify-content-between"
                            onClick={() => setWorkLogActiveTab('complete')}
                              >
                                <span>Complete</span>
                            <Badge bg={workLogActiveTab === 'complete' ? 'light' : 'success'} text={workLogActiveTab === 'complete' ? 'success' : 'light'} className="py-1 px-2">
                                  {residents.length}
                                </Badge>
                              </Button>
                              <Button
                            variant={workLogActiveTab === 'pending' ? 'warning' : 'light'}
                                size="sm"
                                className="text-start d-flex align-items-center justify-content-between"
                            onClick={() => setWorkLogActiveTab('pending')}
                              >
                                <span>Pending</span>
                            <Badge bg={workLogActiveTab === 'pending' ? 'light' : 'warning'} text={workLogActiveTab === 'pending' ? 'warning' : 'light'} className="py-1 px-2">
                                  {pendingResidents.length}
                                </Badge>
                              </Button>
                              <Button
                            variant={workLogActiveTab === 'incomplete' ? 'danger' : 'light'}
                                size="sm"
                                className="text-start d-flex align-items-center justify-content-between"
                            onClick={() => setWorkLogActiveTab('incomplete')}
                              >
                                <span>Incomplete</span>
                            <Badge bg={workLogActiveTab === 'incomplete' ? 'light' : 'danger'} text={workLogActiveTab === 'incomplete' ? 'danger' : 'light'} className="py-1 px-2">
                                  {incompleteResidents.length}
                                </Badge>
                              </Button>
                            </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                          </div>
              </Col>

              <Col lg={10} md={9} sm={12}>
                <Tab.Container activeKey={workLogActiveTab} onSelect={key => setWorkLogActiveTab(key)}>
                  {showHeatmap && (() => {
                    const weekStarts = getWeekStartsForHeatmap();
                    const selectedWeekStart = getSelectedWeekMoment();
                    const selectedWeekLabel = selectedWeekStart
                      ? `${selectedWeekStart.format("MMM D")} - ${selectedWeekStart.clone().add(6, "days").format("MMM D, YYYY")}`
                      : "No data";
                    const canGoPrev = weeklyHeatmapOffset > 0;
                    const canGoNext = weeklyHeatmapOffset < weekStarts.length - 1;

                    return (
                      <Card className="shadow-sm border-0 mb-3">
                        <CardBody className="p-4 pt-3">
                          <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
                              <h6 className="fw-semibold">Resident Heatmap</h6>
                              <div className="text-muted text-nowrap">{selectedWeekLabel}</div>
                              <div className="radio-inputs" style={{width: 'fit-content', maxWidth: '80px' }}>
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="week-radio-prev"
                                    disabled={!canGoPrev}
                                    onClick={() => setWeeklyHeatmapOffset((prev) => Math.max(prev - 1, 0))}
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-left"></i>
                                  </span>
                                </label>
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="week-radio-next"
                                    disabled={!canGoNext}
                                    onClick={() =>
                                      setWeeklyHeatmapOffset((prev) => Math.min(prev + 1, Math.max(weekStarts.length - 1, 0)))
                                    }
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-right"></i>
                                  </span>
                                </label>
                              </div>
                          </div>

                          {!canLoadHeatmap ? (  
                            <div className="text-muted small py-2">Heatmap will load after dashboard data.</div>
                          ) : loadingWeeklyHeatmap ? (
                            <div className="text-muted small py-2">Loading weekly logs...</div>
                          ) : heatmapResidents.length === 0 ? (
                            <div className="text-muted small py-2">No residents match the current filters.</div>
                          ) : (
                            <div className="row g-3">
                              {/* Heatmap col-10 */}
                              <div className="col-12 col-lg-10 col-md-9" style={{ overflowX: "auto" }}>
                                <div className="d-flex" style={{ minWidth: 420 }}>
                                  <div
                                    className="d-flex flex-column me-2"
                                    style={{ minWidth: 20, gap: 4, marginTop: 26 }}
                                  >
                                    {heatmapDayConfig.map((day) => (
                                      <div
                                        key={`day-label-${day.fullLabel}`}
                                        className="d-flex align-items-center justify-content-end pe-1 text-muted small"
                                        style={{ height: 20, fontSize: 12 }}
                                        title={day.fullLabel}
                                      >
                                        {day.label}
                                      </div>
                                    ))}
                                  </div>

                                  {heatmapResidents.map((resident) => (
                                    <div
                                      key={`heat-col-${resident.id}`}
                                      className="d-flex flex-column align-items-center me-1"
                                      style={{ gap: 4 }}
                                    >
                                      <div
                                        className="small fw-semibold text-uppercase"
                                        style={{
                                          minHeight: 20,
                                          width: 20,
                                          fontSize: 12,
                                          display: "flex",
                                          alignItems: "baseline",
                                          justifyContent: "center",
                                          transform: "rotate(-90deg)",
                                          transformOrigin: "center center",
                                          whiteSpace: "nowrap"
                                        }}
                                        title={`${resident.firstName} ${resident.lastName}`}
                                      >
                                        {getResidentInitials(resident)}
                                      </div>
                                      {heatmapDayConfig.map((day, dayIndex) => {
                                        const dayData = getResidentDayHours(resident.id, day.sourceIndex, selectedWeekStart);
                                        const bg = getHeatColor(dayData.hours);
                                        const textColor = getHeatTextColor(dayData.hours);
                                        const tooltipText = (
                                          <>
                                            <div className="fw-semibold">{resident.firstName} {resident.lastName}</div>
                                            <div>{day.fullLabel}: {dayData.display}</div>
                                            <div>Week total: {dayData.weekTotal}</div>
                                          </>
                                        );

                                        return (
                                          <OverlayTrigger
                                            key={`heat-cell-${resident.id}-${dayIndex}`}
                                            placement="top"
                                            overlay={<Tooltip id={`tooltip-${resident.id}-${dayIndex}`}>{tooltipText}</Tooltip>}
                                          >
                                            <div
                                              role="button"
                                              aria-label={tooltipText}
                                              className="rounded"
                                              style={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: bg,
                                                color: textColor,
                                              }}
                                            />
                                          </OverlayTrigger>
                                        );
                                      })}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {/* Legend col-2 */}
                              <div className="col-12 col-lg-2 col-md-3">
                                <div className="d-flex flex-md-column flex-row gap-md-2 gap-3 pt-1 align-items-end">
                                  <div className="small fw-semibold">Legend</div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="rounded-1" style={{ width: 12, height: 12, backgroundColor: "var(--green)"}} />
                                    <span className="small text-muted">{"<= 8h"}</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="rounded-1" style={{ width: 12, height: 12, backgroundColor: "var(--yellow)"}} />
                                    <span className="small text-muted">8-10h</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="rounded-1" style={{ width: 12, height: 12, backgroundColor: "var(--red)"}} />
                                    <span className="small text-muted">{"> 10h"}</span>
                                  </div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span className="rounded-1" style={{ width: 12, height: 12, backgroundColor: "var(--gray-200)"}} />
                                    <span className="small text-muted">None</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    );
                  })()}
                  <Tab.Content>
                    <Tab.Pane eventKey="complete" active={workLogActiveTab === 'complete'}>
                      <Card className="shadow-sm border-0">
                        <CardHeader className="px-4 py-3 border-0 d-flex flex-column flex-md-row justify-content-between">
                          <div className="d-flex flex-column mb-2 mb-md-0">
                            <h6 className="mb-0 fw-semibold">Completed</h6>
                            <p className="text-muted small mb-0">showing {residents.length} completed submissions</p>
                          </div>
                          <div className="d-flex flex-row align-items-start justify-content-start gap-2">
                               <Form.Group>
                                <Form.Select 
                                  size="sm"
                                  value={filterPGY} 
                                  onChange={e => setFilterPGY(e.target.value)}
                                >
                                  <option value="All">All Years</option>
                                  {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                              <Form.Group>
                                <Form.Control
                                  type="text"
                                  placeholder="Search by name..."
                                  size="sm"
                                  value={search}
                                  onChange={e => setSearch(e.target.value)}
                                />
                              </Form.Group>
                             
                            
         
                          {/* Desktop month navigation */}
                          <div className="d-none d-md-flex align-items-center gap-2">
                           
                          
                            {(month !== new Date().getMonth() || year !== new Date().getFullYear()) && (
                              <Button
                                variant="light-solid"
                                size="sm"
                                onClick={() => {
                                  const today = new Date();
                                  handleMonthChange(today.getMonth() - month);
                                }}
                              >
                                <span className="d-md-block d-lg-none"><i className="bi bi-calendar-date"></i></span>
                                <span className="d-lg-block d-none">Current</span>
                              </Button>
                            )} 
                            <div className="radio-inputs">
                              <label className="radio">
                                <input
                                  type="button"
                                  name="month-radio-prev"
                                  onClick={() => handleMonthChange(-1)}
                                />
                                <span className="name bg-white-hover px-2">
                                  <i className="bi bi-chevron-left"></i>
                                </span>
                              </label>
                              <div className="dropdown-container">
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light-solid"
                                    size="sm"
                                    align="end"
                                  >
                                    {moment().month(month).year(year).format("MMMM YYYY")}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    {(() => {
                                      const currentYear = new Date().getFullYear();
                                      const months = [];
                                      
                                      // Generate months for current year
                                      for (let i = 0; i < 12; i++) {
                                        months.push({
                                          value: i,
                                          year: currentYear,
                                          label: new Date(currentYear, i).toLocaleString('default', { month: 'long' })
                                        });
                                      }
                                      
                                      // Add next year's months if current month is close to end of year
                                      if (month >= 8) { // September or later
                                        for (let i = 0; i < 12; i++) {
                                          months.push({
                                            value: i,
                                            year: currentYear + 1,
                                            label: new Date(currentYear + 1, i).toLocaleString('default', { month: 'long' })
                                          });
                                        }
                                      }
                                      
                                      return months;
                                    })().map((monthOption) => (
                                      <Dropdown.Item
                                        key={`${monthOption.year}-${monthOption.value}`}
                                        onClick={() => {
                                          const yearDiff = monthOption.year - year;
                                          const monthDiff = monthOption.value - month;
                                          handleMonthChange(monthDiff + (yearDiff * 12));
                                        }}
                                        active={monthOption.value === month && monthOption.year === year}
                                      >
                                        {monthOption.label} {monthOption.year}
                                      </Dropdown.Item>
                                    ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                              <label className="radio">
                                <input
                                  type="button"
                                  name="month-radio-next"
                                  onClick={() => handleMonthChange(1)}
                                />
                                <span className="name bg-white-hover px-2">
                                  <i className="bi bi-chevron-right"></i>
                                </span>
                              </label>
                          
                            </div>
                            </div>
                          
                          {/* Mobile month dropdown */}
                          <div className="d-md-none">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="w-100"
                              >
                                {moment().month(month).year(year).format("MMMM YYYY")}
                              </Dropdown.Toggle>
                              <Dropdown.Menu style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                width: '100%'
                              }}>
                                {(() => {
                                  const currentYear = new Date().getFullYear();
                                  const months = [];
                                  
                                  // Generate months for current year
                                  for (let i = 0; i < 12; i++) {
                                    months.push({
                                      value: i,
                                      year: currentYear,
                                      label: new Date(currentYear, i).toLocaleString('default', { month: 'long' })
                                    });
                                  }
                                  
                                  // Add next year's months if current month is close to end of year
                                  if (month >= 8) { // September or later
                                    for (let i = 0; i < 12; i++) {
                                      months.push({
                                        value: i,
                                        year: currentYear + 1,
                                        label: new Date(currentYear + 1, i).toLocaleString('default', { month: 'long' })
                                      });
                                    }
                                  }
                                  
                                  return months;
                                })().map((monthOption) => (
                                  <Dropdown.Item
                                    key={`${monthOption.year}-${monthOption.value}`}
                                    onClick={() => {
                                      const yearDiff = monthOption.year - year;
                                      const monthDiff = monthOption.value - month;
                                      handleMonthChange(monthDiff + (yearDiff * 12));
                                    }}
                                    active={monthOption.value === month && monthOption.year === year}
                                    style={{
                                      padding: '8px 16px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {monthOption.label} {monthOption.year}
                                  </Dropdown.Item>
                                ))}
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                    </div>
                      </CardHeader>
                        <CardBody className="p-0">
                          {residents.length > 0 ? (
                            <>
                              <div className="table-responsive">
                                <Table hover>
                                  <thead>
                                    <tr className="custom-header-bg">
                                      <th>User</th>
                                      <th>PGY Year</th>
                                      <th>Rotation</th>
                                      <th>Status</th>
                                      <th>Last Submission</th>
                                      <th>Hr Compliance</th>
                                      <th>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {completePageResidents.map((r, i) => {
                                      return (
                                        <tr key={`complete-${i}`} onClick={() => handleViewClick(r.id)} style={{ cursor: 'pointer' }}>
                                          <td> <div className="d-flex flex-row align-items-center gap-2"><UserAvatar user={r} size={32} alt="" /> <span>{r.firstName} {r.lastName}</span></div></td>
                                          <td>{r.year || "N/A"}</td>
                                          <td>{getResidentRotation(r)}</td>
                                          <td>
                                            <Badge bg="success" text="success" className="fw-medium">
                                              Complete
                                            </Badge>
                                          </td>
                                          <td>
                                            {r.WorkHourSubmissions?.[0]?.createdAt ?
                                              moment(r.WorkHourSubmissions[0].createdAt).format("MMM D, YYYY") :
                                              "N/A"
                                            }
                                          </td>
                                          <td>
                                            {r.hasViolation ? <Badge bg="danger" className="rounded-2"> Non-Compliant</Badge> : 
                                            <Badge bg="success" className="rounded-2"> Compliant</Badge>}
                                          </td>
                                          <td>
                                            <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                                              <Dropdown.Toggle variant="outline-primary" size="sm">
                                                Actions
                                              </Dropdown.Toggle>
                                              <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => handleOpenReportModal(r)}>
                                                  Generate Report
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleOpenUnlockConfirm(r)}>
                                                  Unlock Submission
                                                </Dropdown.Item>
                                              </Dropdown.Menu>
                                            </Dropdown>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-5">
                              <div className="text-muted">
                                <i className="bi bi-calendar-check fs-1 mb-3 d-block"></i>
                                No complete submissions found.
                              </div>
                            </div>
                          )}
                        </CardBody>
                      </Card>

                      {/* Pagination for Completed Submissions */}
                      {residents.length > 0 && (

                          <Pagination 
                            currentPage={currentPage}
                            totalPages={totalPages(getFilteredResidents())}
                            onPageChange={(page) => handlePageChange(page, setCurrentPage)}
                          />

                      )}

                      {/* Unlock Requests Card */}
                      {loadingUnlockRequests ? (
                        <Card className="shadow-sm border-0 mt-4">
                          <CardBody className="p-4 text-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span className="text-muted">Loading unlock requests...</span>
                          </CardBody>
                        </Card>
                      ) : unlockRequests.length > 0 && (
                        <Card className="shadow-sm border-0 mt-4">
                          <CardHeader className="px-4 py-3 border-0">
                            <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                              <span className=" bg-warning px-3 py-2 rounded-3 d-flex align-items-center justify-content-center"><i className="bi bi-unlock text-warning"></i></span>
                              <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-semibold">
                                  Unlock Requests
                                </h6>
                                <p className="text-muted small mb-0">showing {unlockRequests.length} unlock requests</p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardBody className="p-0">
                            <div className="table-responsive">
                              <Table hover className="mb-0">
                                <thead>
                                  <tr className="custom-header-bg">
                                    <th className="px-4">Resident</th>
                                    <th>PGY Year</th>
                                    <th>Month</th>
                                    <th>Requested</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unlockRequests.map((request, i) => (
                                    <tr key={`unlock-request-${i}`}>
                                      <td className="px-4">
                                        {request.resident ? 
                                          `${request.resident.firstName} ${request.resident.lastName}` : 
                                          'Unknown Resident'}
                                      </td>
                                      <td>{request.resident?.year || "N/A"}</td>
                                      <td>{request.month || (request.submission?.weekStart ? moment(request.submission.weekStart).format("MMMM YYYY") : "N/A")}</td>
                                      <td>{moment(request.requestedAt).format("MMM D, YYYY")}</td>
                                      <td>
                                        <Dropdown align="end">
                                          <Dropdown.Toggle variant="light-lite" size="sm" className="no-arrow">
                                            <i className="bi bi-three-dots-vertical"></i>
                                          </Dropdown.Toggle>
                                          <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleApproveUnlockRequest(request)}> <i className="bi bi-check-circle me-1"></i> Approve</Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleDenyUnlockRequest(request)}> <i className="bi bi-x-circle me-1"></i> Deny</Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                       
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </CardBody>
                        </Card>
                      )}

                      {/* Work Hour Violations Card */}
                      {residents.filter(r => r.hasViolation).length > 0 && (
                        <Card className="shadow-sm border-0 mt-4">
                          <CardHeader className="p-4 border-0">
                            <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                              <span className=" bg-danger px-3 py-2 rounded-3 d-flex align-items-center justify-content-center"><i className="bi bi-exclamation-triangle text-danger"></i></span>
                            <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-semibold">
                                  
                                  Work Hour Violations
                                </h6>                           
                                <p className="text-muted small mb-0">showing {residents.filter(r => r.hasViolation).length} work hour violations</p>
                              </div>
                              </div>
                          </CardHeader>
                          <CardBody className="p-0">
                            <div className="table-responsive">
                              <Table hover className="mb-0">
                                <thead>
                                  <tr className="custom-header-bg">
                                    <th className="px-4">Resident</th>
                                    <th>PGY Year</th>
                                    <th>Rotation</th>
                                    <th>Month Violations</th>
                                    <th>Week Violations</th>
                                    <th>Hours Worked</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getPaginatedData(getFilteredViolations(), currentViolationPage, setCurrentViolationPage).map((resident, i) => {
                                    const totalViolations = resident.violationWeeks.length;
                                    const totalOverLimit = resident.violationWeeks.reduce((sum, v) => sum + v.overLimit, 0);
                                    const maxHours = Math.max(...resident.violationWeeks.map(v => v.hours * 60 + v.minutes));
                                    const maxViolation = resident.violationWeeks.find(v => (v.hours * 60 + v.minutes) === maxHours);
                                    
                                    return (
                                      <tr key={`violation-${i}`}>
                                        <td className="px-4">{resident.firstName} {resident.lastName}</td>
                                        <td>{resident.year || "N/A"}</td>
                                        <td>{getResidentRotation(resident)}</td>
                                        <td>
                                          <Badge 
                                            bg={totalViolations >= 3 ? "danger" : totalViolations >= 2 ? "warning" : "secondary"}
                                            className="fw-medium"
                                          >
                                            {totalViolations}
                                          </Badge>
                                        </td>
                                        <td>
                                          <div className="small">
                                            {resident.violationWeeks.map((violation, j) => {
                                              const startMoment = moment(violation.weekStart);
                                              const endMoment = moment(violation.weekEnd);
                                              
                                              console.log('Display dates for violation', j, ':', {
                                                startDate: violation.weekStart,
                                                endDate: violation.weekEnd,
                                                startFormatted: startMoment.format('MMM D'),
                                                endFormatted: endMoment.format('MMM D'),
                                                startMonth: startMoment.format('MM'),
                                                endMonth: endMoment.format('MM')
                                              });
                                              
                                              return (
                                                <div key={j} className="mb-1">
                                                  <div>
                                                    {startMoment.format('MMM D')} - {endMoment.format('MMM D')}
                                                  </div>
                                                  <div className="text-muted small">
                                                    Week {startMoment.format('w')} • {startMoment.format('YYYY')}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="small">
                                            {resident.violationWeeks.map((violation, j) => {
                                              const totalMinutes = (violation.hours || 0) * 60 + (violation.minutes || 0);
                                              const overLimitMinutes = Math.max(violation.overLimit || 0, 0);
                                              const compliantMinutes = Math.max(totalMinutes - overLimitMinutes, 0);
                                              const totalForBar = Math.max(totalMinutes, 1);
                                              const compliantPercent = (compliantMinutes / totalForBar) * 100;
                                              const overLimitPercent = (overLimitMinutes / totalForBar) * 100;

                                              return (
                                                <div key={j} className="mb-2">
                                                  <div
                                                    className="mt-1 rounded"
                                                    style={{
                                                      height: 8,
                                                      backgroundColor: 'var(--bs-gray-200, #e9ecef)',
                                                      minWidth: 140,
                                                      overflow: 'hidden'
                                                    }}
                                                  >
                                                    <div
                                                      className="d-flex"
                                                      style={{ height: '100%', width: '100%' }}
                                                    >
                                                      <div
                                                        style={{
                                                          height: '100%',
                                                          width: `${Math.min(100, Math.max(0, compliantPercent))}%`,
                                                          backgroundColor: 'var(--bs-success, #198754)',
                                                          transition: 'width 0.2s ease'
                                                        }}
                                                      />
                                                      {overLimitMinutes > 0 && (
                                                        <div
                                                          style={{
                                                            height: '100%',
                                                            width: `${Math.min(100, Math.max(0, overLimitPercent))}%`,
                                                            backgroundColor: 'var(--bs-danger, #dc3545)',
                                                            transition: 'width 0.2s ease'
                                                          }}
                                                        />
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div className="d-flex justify-content-between mt-1">
                                                    <span className="text-muted">
                                                      {Math.floor(compliantMinutes / 60)}h {compliantMinutes % 60}m compliant
                                                    </span>
                                                    {overLimitMinutes > 0 && (
                                                      <span className="text-danger">
                                                        +{Math.floor(overLimitMinutes / 60)}h {overLimitMinutes % 60}m over
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </td>
                                       
                                        <td>
                                          <Button 
                                            variant="outline-primary" 
                                            size="sm"
                                            onClick={() => handleViewClick(resident.id)}
                                          >
                                            View Details
                                          </Button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          </CardBody>
                        </Card>
                      )}

                      {/* Pagination for Work Hour Violations */}
                      {residents.filter(r => r.hasViolation).length > 0 && getFilteredViolations().length > 0 && (

                          <Pagination 
                            currentPage={currentViolationPage}
                            totalPages={totalPages(getFilteredViolations())}
                            onPageChange={(page) => handlePageChange(page, setCurrentViolationPage)}
                          />

                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="pending" active={workLogActiveTab === 'pending'}>
                      <Card className="shadow-sm border-0">
                        <CardHeader className="px-4 py-3 border-0 d-flex flex-column flex-md-row justify-content-between">
                            <div className="d-flex flex-column mb-2 mb-md-0">
                              <h6 className="mb-0 fw-semibold">
                                Pending
                              </h6>
                              <p className="text-muted small mb-0">showing {pendingResidents.length} pending submissions</p>
                            </div>
                              <div className="d-flex flex-row align-items-start justify-content-start gap-2">
                                <Form.Select 
                                    value={filterPGY} 
                                    onChange={e => setFilterPGY(e.target.value)}
                                    size="sm"
                                    style={{ maxWidth: '110px' }}
                                  >
                                    <option value="All">All Years</option>
                                    {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </Form.Select>
                                <Form.Group>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search by name..."
                                    size="sm"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                  />
                                </Form.Group>
                                <Form.Group>
                                  
                                </Form.Group>
                              
                              {/* Desktop month navigation */}
                              <div className="d-none d-md-flex align-items-center gap-2">
                                {(month !== new Date().getMonth() || year !== new Date().getFullYear()) && (
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => {
                                      const today = new Date();
                                      handleMonthChange(today.getMonth() - month);
                                    }}
                                  >
                                    <span className="d-md-block d-lg-none"><i className="bi bi-calendar-date"></i></span>
                                    <span className="d-lg-block d-none">Current</span>
                                  </Button>
                                )}
                                <div className="radio-inputs">
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="month-radio-prev"
                                    onClick={() => handleMonthChange(-1)}
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-left"></i>
                                  </span>
                                </label>
                                <div className="dropdown-container">
                                  <Dropdown>
                                    <Dropdown.Toggle
                                      variant="light-solid"
                                      size="sm"

                                    >
                                      {moment().month(month).year(year).format("MMMM YYYY")}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      {Array.from({ length: 12 }, (_, i) => {
                                        const date = new Date();
                                        date.setMonth(i);
                                        return {
                                          value: date.getMonth(),
                                          label: date.toLocaleString('default', { month: 'long' })
                                        };
                                      }).map((monthOption) => (
                                        <Dropdown.Item
                                          key={monthOption.value}
                                          onClick={() => handleMonthChange(monthOption.value - month)}
                                          active={monthOption.value === month}
                                        >
                                          {monthOption.label} {year}
                                        </Dropdown.Item>
                                      ))}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="month-radio-next"
                                    onClick={() => handleMonthChange(1)}
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-right"></i>
                                  </span>
                                </label>
                              </div>
                            </div>
                              
                              {/* Mobile month dropdown */}
                              <div className="d-md-none">
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    className="w-100"
                                  >
                                    {moment().month(month).year(year).format("MMMM YYYY")}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    width: '100%'
                                  }}>
                                    {Array.from({ length: 12 }, (_, i) => {
                                      const date = new Date();
                                      date.setMonth(i);
                                      return {
                                        value: date.getMonth(),
                                        label: date.toLocaleString('default', { month: 'long' })
                                      };
                                    }).map((monthOption) => (
                                      <Dropdown.Item
                                        key={monthOption.value}
                                        onClick={() => handleMonthChange(monthOption.value - month)}
                                        active={monthOption.value === month}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {monthOption.label} {year}
                                      </Dropdown.Item>
                                    ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                              </div>
                        </CardHeader>
                                                <CardBody className="p-0">
                          {pendingResidents.length > 0 ? (
                            <>
                            <div className="table-responsive">
                          <Table hover>
                            <thead>
                              <tr className="custom-header-bg">
                                <th>User</th>
                                <th>PGY Year</th>
                                <th>Rotation</th>
                                <th>Status</th>
                                <th>Last Saved</th>
                              </tr>
                            </thead>
                            <tbody>
                                    {getPaginatedData(
                                      pendingResidents
                                .filter(r => {
                                  if (!search.trim()) return true;
                                  const s = search.toLowerCase();
                                  return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
                                })
                                        .filter(r => filterPGY === 'All' || String(r.year) === filterPGY),
                                      currentPage,
                                      setCurrentPage
                                    ).map((r, i) => (
                                  <tr key={`pending-${i}`} onClick={() => handleViewClick(r.id)} style={{ cursor: 'pointer' }}>
                                    <td> <div className="d-flex flex-row align-items-center gap-2"><UserAvatar user={r} size={32} alt="" /> <span>{r.firstName} {r.lastName}</span></div></td>
                                    <td>{r.year || "N/A"}</td>
                                    <td>{getResidentRotation(r)}</td>
                                    <td>
                                      <Badge bg="warning-subtle" text="warning" className="fw-medium">
                                        Pending
                                      </Badge>
                                    </td>
                                    <td className="p-3 text-dark">
                                      {r.WorkHourSubmissions?.[0]?.createdAt ?
                                        moment(r.WorkHourSubmissions[0].createdAt).format("MMM D, YYYY") :
                                        r.lastSavedDate ?
                                          moment(r.lastSavedDate, "MM/DD/YYYY").format("MMM D, YYYY") :
                                          "N/A"
                                      }
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                                                        </div>
                            </>
                          ) : (
                            <div className="text-center py-5">
                              <div className="text-muted">
                                <i className="bi bi-clock fs-1 mb-3 d-block"></i>
                                No pending submissions found.
                              </div>
                            </div>
                          )}
                        </CardBody> 
                      </Card>

                      {/* Pagination for Pending Submissions */}
                      {pendingResidents.length > 0 && (

                          <Pagination 
                            currentPage={currentPage}
                            totalPages={totalPages(
                              pendingResidents
                                .filter(r => {
                                  if (!search.trim()) return true;
                                  const s = search.toLowerCase();
                                  return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
                                })
                                .filter(r => filterPGY === 'All' || String(r.year) === filterPGY)
                            )}
                            onPageChange={(page) => handlePageChange(page, setCurrentPage)}
                          />

                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="incomplete" active={workLogActiveTab === 'incomplete'}>
                      <Card className="shadow-sm border-0">
                        <CardHeader className="px-4 py-3 border-0 d-flex flex-column flex-md-row justify-content-between">
                          <div className="d-flex flex-column mb-2 mb-md-0">
                            <h6 className="mb-0 fw-semibold">Incomplete</h6>
                            <p className="text-muted small mb-0">showing {incompleteResidents.length} incomplete submissions</p>
                          </div>
                          <div className="d-flex flex-row align-items-start justify-content-start gap-2">
                            <Form.Group>
                              <Form.Select 
                                value={filterPGY} 
                                onChange={e => setFilterPGY(e.target.value)}
                                size="sm"
                                style={{ maxWidth: '110px' }}
                              >
                                <option value="All">All Years</option>
                                  {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                              <Form.Group>
                                <Form.Control
                                  type="text"
                                  placeholder="Search by name..."
                                  size="sm"
                                  value={search}
                                  onChange={e => setSearch(e.target.value)}
                                />
                              </Form.Group>
                              {/* Desktop month navigation */}
                              <div className="d-none d-md-flex align-items-center gap-2">
                                {(month !== new Date().getMonth() || year !== new Date().getFullYear()) && (
                                  <Button
                                    variant="light-solid"
                                    size="sm"
                                    onClick={() => {
                                      const today = new Date();
                                      handleMonthChange(today.getMonth() - month);
                                    }}
                                  >
                                    <span className="d-md-block d-lg-none"><i className="bi bi-calendar-date"></i></span>
                                    <span className="d-lg-block d-none">Current</span>
                                  </Button>
                                )}
                                <div className="radio-inputs">
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="month-radio-prev"
                                    onClick={() => handleMonthChange(-1)}
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-left"></i>
                                  </span>
                                </label>
                                <div className="dropdown-container">
                                  <Dropdown>
                                    <Dropdown.Toggle
                                      variant="light-solid"
                                      size="sm"
                                      align="end"
                                    >
                                      {moment().month(month).year(year).format("MMMM YYYY")}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      {Array.from({ length: 12 }, (_, i) => {
                                        const date = new Date();
                                        date.setMonth(i);
                                        return {
                                          value: date.getMonth(),
                                          label: date.toLocaleString('default', { month: 'long' })
                                        };
                                      }).map((monthOption) => (
                                        <Dropdown.Item
                                          key={monthOption.value}
                                          onClick={() => handleMonthChange(monthOption.value - month)}
                                          active={monthOption.value === month}
                                          style={{
                                            padding: '8px 16px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          {monthOption.label} {year}
                                        </Dropdown.Item>
                                      ))}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                                <label className="radio">
                                  <input
                                    type="button"
                                    name="month-radio-next"
                                    onClick={() => handleMonthChange(1)}
                                  />
                                  <span className="name bg-white-hover px-2">
                                    <i className="bi bi-chevron-right"></i>
                                  </span>
                                </label>
                              </div>
                            </div>
                           
                                
                              {/* Mobile month dropdown */}
                              <div className="d-md-none">
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    className="w-100"
                                  >
                                    {moment().month(month).year(year).format("MMMM YYYY")}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    width: '100%'
                                  }}>
                                    {Array.from({ length: 12 }, (_, i) => {
                                      const date = new Date();
                                      date.setMonth(i);
                                      return {
                                        value: date.getMonth(),
                                        label: date.toLocaleString('default', { month: 'long' })
                                      };
                                    }).map((monthOption) => (
                                      <Dropdown.Item
                                        key={monthOption.value}
                                        onClick={() => handleMonthChange(monthOption.value - month)}
                                        active={monthOption.value === month}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {monthOption.label} {year}
                                      </Dropdown.Item>
                                    ))}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                        </CardHeader>
                        <CardBody className="p-0">
                          {incompleteResidents.length > 0 ? (
                            <>
                            <div className="table-responsive">
                          <Table hover>
                            <thead>
                              <tr className="custom-header-bg">
                                <th>User</th>
                                <th>PGY Year</th>
                                <th>Rotation</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                                    {getPaginatedData(
                                      incompleteResidents
                                .filter(r => {
                                  if (!search.trim()) return true;
                                  const s = search.toLowerCase();
                                  return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
                                })
                                        .filter(r => filterPGY === 'All' || String(r.year) === filterPGY),
                                      currentPage,
                                      setCurrentPage
                                    ).map((r, i) => (
                                  <tr key={`incomplete-${i}`}>
                                    <td> <div className="d-flex flex-row align-items-center gap-2"><UserAvatar user={r} size={32} alt="" /> <span>{r.firstName} {r.lastName}</span></div></td>
                                    <td>{r.year || "N/A"}</td>
                                    <td>{getResidentRotation(r)}</td>
                                    <td>
                                      <Badge bg="danger-subtle" text="danger" className="fw-medium">
                                        Incomplete
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                                                        </div>
                            </>
                          ) : (
                          <div className="text-center py-5">
                            <div className="text-muted">
                              <i className="bi bi-calendar-x fs-1 mb-3 d-block"></i>
                                No incomplete submissions found.
                            </div>
                          </div>
                        )}
                        </CardBody>
                    </Card>

                    {/* Pagination for Incomplete Submissions */}
                    {incompleteResidents.length > 0 && (

                        <Pagination 
                          currentPage={currentPage}
                          totalPages={totalPages(
                            incompleteResidents
                              .filter(r => {
                                if (!search.trim()) return true;
                                const s = search.toLowerCase();
                                return `${r.firstName} ${r.lastName}`.toLowerCase().includes(s);
                              })
                              .filter(r => filterPGY === 'All' || String(r.year) === filterPGY)
                          )}
                          onPageChange={(page) => handlePageChange(page, setCurrentPage)}
                        />

                    )}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane eventKey="vacation" active={activeTab === 'vacation'}>
              <Card className="shadow-sm border-0">
                <CardHeader className="p-md-4 border-bottom-0 " style={{backgroundColor: "var(--bs-card-bg)"}}>
                    <div className=" d-flex flex-column flex-md-row justify-content-between mb-2 lg-mb-0 mb-md-0 mb-sm-2">
                        <div className="d-flex flex-column mb-2 mb-md-0 justify-content-start">
                        <h6 className="mb-0 fw-semibold">Leave Requests</h6>
                        <span className="text-muted small">
                          Showing {previousRequests.length} previous and {newRequests.length} new requests
                        </span>
                        </div>
                        <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                        {(filterPGY !== 'All' || filterStatus !== 'All' || filterType !== 'All') && (
                          <Button variant="light-solid" size="sm" className="d-flex align-items-center justify-content-center" onClick={() => { setFilterPGY('All'); setFilterStatus('All'); setFilterType('All'); }}>
                            <i className="bi bi-x-lg"></i><span className="ms-1 d-none d-md-inline-block">Clear</span>
                          </Button>
                        )} 
                        <Dropdown>
                          <Dropdown.Toggle variant="light" size="sm" autoClose="outside">
                            <i className="bi bi-funnel"></i> Filter
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Form className="p-2 d-flex flex-column gap-2" style={{ minWidth: 180 }}>
                              <Form.Group>
                                <FloatingLabel label="PGY Year">
                                <Form.Select value={filterPGY} onChange={e => setFilterPGY(e.target.value)}>
                                  <option value="All">All Years</option>
                                  {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Form.Select>
                                </FloatingLabel>
                                </Form.Group>
                                <Form.Group>
                                  <FloatingLabel label="Status">
                                <Form.Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                  <option value="All">All Statuses</option>
                                  <option value="Pending">Pending</option>
                                  <option value="Approved">Approved</option>
                                  <option value="Denied">Denied</option>
                                </Form.Select>
                                </FloatingLabel>
                                </Form.Group>
                                <Form.Group>
                                  <FloatingLabel label="Type">
                                <Form.Select value={filterType} onChange={e => setFilterType(e.target.value)}>
                                  <option value="All">All Types</option>
                                  <option value="Vacation">Vacation</option>
                                  <option value="Sick">Sick</option>
                                </Form.Select>
                                </FloatingLabel>
                                </Form.Group>
                            </Form>
                          </Dropdown.Menu>
                        </Dropdown>
                       
       
                        <Form.Control
                          type="text"
                          placeholder="Search by resident, reason, or date..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                        />
                        </div>
                        </div>

                    </CardHeader>
                    <Card.Body className="p-0">

                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : (
                  <>
                    

                    <Table responsive hover>
                      <thead>
                        <tr className="custom-header-bg">
                          <th>Resident</th>
                          <th>PGY Year</th>
                          <th>Type</th>
                          <th>Date Range</th>
                          <th>Days</th>
                          <th>Status</th>
                          <th>Requested</th>
                          <th>Denial Reason</th>
                          <th>Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRequests.length === 0 ? (
                          <tr><td colSpan={9} className="text-center text-muted"><i className="bi bi-calendar-x fs-1 mb-3 d-block"></i> No requests found.</td></tr>
                        ) : (
                          <>
                            {/* New Requests Section */}
                            {newRequests.length > 0 && (
                              <>
                                <tr className="table-info">
                                  <td colSpan={9} className="ps-4 fw-bold">
                                    New Requests 
                                    <Badge bg="warning" className="ms-2">
                                      {newRequests.length}
                                    </Badge>
                                  </td>
                                </tr>
                                {newRequests.map(r => {
                                  const daysBreakdown = calculateWorkdaysAndWeekends(r.startDate, r.endDate);
                                  return (
                                    <tr 
                                      key={r.id} 
                                      className="table-warning" 
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => { setSelectedRequest(r); setShowApproveModal(true); }}
                                    >
                                      <td className="ps-4">
                                        <div className="d-flex align-items-center gap-2">
                                          <UserAvatar user={r.resident} size={32} alt="" />
                                          <span>{r.resident ? `${r.resident.firstName} ${r.resident.lastName}` : ''}</span>
                                        </div>
                                      </td>
                                      <td>{r.resident ? `${r.resident.year}` : ''}</td>
                                      <td>
                                        <span className={`badge ${r.type === 'Sick' ? 'text-dark' : 'bg-info text-dark'}`}>{r.type}</span>
                                      </td>
                                      <td>{moment(r.startDate).format('YYYY-MM-DD')} to {moment(r.endDate).format('YYYY-MM-DD')}</td>
                                      <td>
                                        <span className="fw-medium">{daysBreakdown}</span>
                                      </td>
                                      <td>
                                        <span className="badge bg-secondary">
                                          {r.status}
                                        </span>
                                      </td>
                                      <td>{moment(r.createdAt).format('YYYY-MM-DD')}</td>
                                      <td>{r.status === 'Denied' ? r.reason : ''}</td>
                                      <td>
                                        <Button 
                                          variant="outline-primary" 
                                          size="sm" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDetailModal({ show: true, request: r });
                                          }}
                                        >
                                          View
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                            
                            {/* Previous Requests Section */}
                            {previousRequests.length > 0 && (
                              <>
                                <tr className="table-light">
                                  <td colSpan={9} className="ps-4 fw-bold">
                                    Previous Requests 
                                    <Badge bg="secondary" className="ms-2">
                                      {previousRequests.length}
                                    </Badge>
                                  </td>
                                </tr>
                                {previousRequests.map(r => {
                                  const daysBreakdown = calculateWorkdaysAndWeekends(r.startDate, r.endDate);
                                  return (
                                    <tr key={r.id}>
                                      <td className="ps-4">
                                        <div className="d-flex align-items-center gap-2">
                                          <UserAvatar user={r.resident} size={32} alt="" />
                                          <span>{r.resident ? `${r.resident.firstName} ${r.resident.lastName}` : ''}</span>
                                        </div>
                                      </td>
                                      <td>{r.resident ? `${r.resident.year}` : ''}</td>
                                      <td>
                                        <span className={`badge ${r.type === 'Sick' ? 'text-dark' : 'bg-info text-dark'}`}>{r.type}</span>
                                      </td>
                                      <td>{moment(r.startDate).format('YYYY-MM-DD')} to {moment(r.endDate).format('YYYY-MM-DD')}</td>
                                      <td>
                                        <span className="fw-medium">{daysBreakdown}</span>
                                      </td>
                                      <td>
                                        <span className={`badge ${
                                          r.status === 'Approved' ? 'bg-success' :
                                          r.status === 'Denied' ? 'bg-danger' : 'bg-secondary'
                                        }`}>
                                          {r.status}
                                        </span>
                                      </td>
                                      <td>{moment(r.createdAt).format('YYYY-MM-DD')}</td>
                                      <td>{r.status === 'Denied' ? r.reason : ''}</td>
                                      <td>
                                        <Button 
                                          variant="outline-primary" 
                                          size="sm" 
                                          onClick={() => setDetailModal({ show: true, request: r })}
                                        >
                                          View
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </>
                            )}
                          </>
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
                {/* Detail Modal */}
                <Modal show={detailModal.show} onHide={() => setDetailModal({ show: false, request: null })}>
                  <Modal.Header closeButton className="border-bottom-0">
                    <Modal.Title className="d-flex flex-column justify-content-start align-items-start w-100">
                      <span>Request Details</span>
                      <div className="text-hl"></div>
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {detailModal.request && (
                      <div>
                        <p><strong>Resident:</strong> {detailModal.request.resident ? `${detailModal.request.resident.firstName} ${detailModal.request.resident.lastName}` : ''}</p>
                        <p><strong>Type:</strong> {detailModal.request.type}</p>
                        <p><strong>Status:</strong> {detailModal.request.status}</p>
                        <p><strong>Date Range:</strong> {moment(detailModal.request.startDate).format('YYYY-MM-DD')} to {moment(detailModal.request.endDate).format('YYYY-MM-DD')}</p>
                        <p><strong>Reason:</strong> {detailModal.request.reason}</p>
                        <p><strong>Requested At:</strong> {moment(detailModal.request.createdAt).format('YYYY-MM-DD')}</p>
                        {detailModal.request.status === 'Denied' && (
                          <p><strong>Denial Reason:</strong> {detailModal.request.reason}</p>
                        )}
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer className="border-top-0">
                    <Button variant="secondary" size="sm" onClick={() => setDetailModal({ show: false, request: null })}>
                      Close
                    </Button>
                  </Modal.Footer>
                </Modal>
                </Card.Body>
                </Card>
            </Tab.Pane>
        <Tab.Pane eventKey="leave" active={activeTab === 'leave'}>
          <Row>
            <Col>
                  <Row className="g-3 mb-4">
                    <Col md={3} sm={6} xs={6}>
                      <Card className="border-0 h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: '#e9f2ff',
                              color: 'var(--blue)',
                              fontWeight: 600
                            }}
                          >
                            <i className="bi bi-people-fill"></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-semibold">
                              Total Residents
                            </div>
                            <div className="fs-5 fw-semibold">
                              {totalLeaveResidents}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} sm={6} xs={6}>
                      <Card className="border-0 h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: '#e8f7f1',
                              color: 'var(--green)',
                              fontWeight: 600
                            }}
                          >
                            <i className="bi bi-graph-up"></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-semibold">
                              Avg Vacation Used
                            </div>
                            <div className="fs-5 fw-semibold">
                              {averageVacationUsedDays.toFixed(1)} days
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} sm={6} xs={6}>
                      <Card className="border-0 h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: '#fff4e5',
                              color: '#fd7e14',
                              fontWeight: 600
                            }}
                          >
                            <i className="bi bi-clock-history"></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-semibold">
                              Pending Requests
                            </div>
                            <div className="fs-5 fw-semibold">
                              {totalPendingLeaveRequests}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3} sm={6} xs={6}>
                      <Card className="border-0 h-100 shadow-sm">
                        <Card.Body className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: '#f6e9ff',
                              color: 'var(--indigo)',
                              fontWeight: 600
                            }}
                          >
                            <i className="bi bi-shield-check"></i>
                          </div>
                          <div>
                            <div className="text-muted small fw-semibold">
                              Good Standing
                            </div>
                            <div className="fs-5 fw-semibold">
                              {goodStandingResidentsCount}
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

              {!leaveAllotments || leaveAllotments.length === 0 ? (
                <Card className="shadow-sm border-0">
                  <CardHeader className="px-4 py-3 border-0 d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex flex-column">
                      <h6 className="mb-0 fw-semibold">Leave Allotments</h6>
                      <p className="text-muted small mb-0">No leave records found.</p>
                    </div>
                  </CardHeader>
                  <Card.Body className="p-0">
                    <Table responsive hover>
                    <thead>
                      <tr className="custom-header-bg">
                            <th className="px-4">Resident</th>
                            <th>PGY</th>
                            <th>Year</th>
                            <th>Vacation Leave</th>
                            <th>Sick Leave</th>
                            <th>Status</th>
                            <th>Requests</th>
                            <th className="text-end pe-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={8} className="text-center py-5">
                          <div className="text-muted">
                            <i className="bi bi-calendar-x fs-1 mb-3 d-block"></i>
                            {leaveAllotments === null
                              ? 'Loading...'
                              : 'No leave records found.'}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
                
              ) : (
                <Card className="shadow-sm border-0">
                  <CardHeader className="px-4 py-3 d-flex flex-column flex-md-row justify-content-between border-0">
                    <div className="d-flex flex-column mb-2 mb-md-0 justify-content-start">
                      <span className="fw-semibold">Residents</span>
                      <span className="text-muted small">
                        Showing{' '}
                        {totalLeaveResidents === 0
                          ? 0
                          : (currentAllotmentPage - 1) *
                              allotmentItemsPerPage +
                            1}{' '}
                        to{' '}
                        {Math.min(
                          currentAllotmentPage * allotmentItemsPerPage,
                          totalLeaveResidents
                        )}{' '}
                        of {totalLeaveResidents} residents
                      </span>
                    </div>
                    <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                      {filterPGY !== 'All' && (
                        <Button variant="light-solid" size="sm" className="d-flex align-items-center justify-content-center" onClick={() => { setFilterPGY('All');}}>
                          <i className="bi bi-x-lg"></i><span className="ms-1 d-none d-md-inline-block">Clear</span>
                        </Button>
                      )}
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm" autoClose="outside">
                          <i className="bi bi-funnel"></i> Filter
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="p-2" style={{ minWidth: 220 }}>
                          <div className="d-flex flex-column gap-2">
                            <div>
                              <FloatingLabel label="PGY Year">
                                <Form.Select
                                  value={filterPGY}
                                  onChange={e => setFilterPGY(e.target.value)}
                                  tabIndex={0}
                                >
                                  <option value="All">All PGY Years</option>
                                  {(pgyLevelOptions.length > 0
                                    ? pgyLevelOptions
                                    : Array.from({ length: 5 }, (_, i) => ({
                                        value: i + 1,
                                        label: `PGY-${i + 1}`
                                      })))
                                    .map(option => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                </Form.Select>
                              </FloatingLabel>
                            </div>
                            <div>
                              <FloatingLabel label="Academic Year">
                                <Form.Select
                                  value={filterAcademicYear}
                                  size="sm"
                                  onChange={e =>
                                    setFilterAcademicYear(parseInt(e.target.value, 10))
                                  }
                                  tabIndex={0}
                                >
                                  {Array.from({ length: 5 }, (_, i) => {
                                    const year = moment().year() - i;
                                    return (
                                      <option key={year} value={year}>
                                        {year}-{year + 1}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </FloatingLabel>
                            </div>
                          </div>
                        </Dropdown.Menu>
                      </Dropdown>
                   
                      <Form.Control
                        type="text"
                        placeholder="Search by resident name..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                      
                    <Form.Select
                      value={allotmentItemsPerPage}
                      onChange={e =>
                        handleAllotmentItemsPerPageChange(
                          parseInt(e.target.value, 10)
                        )
                      }
                      style={{ width: 'auto' }}
                      size="sm"
                    >
                      <option value={5}>5 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </Form.Select>
                    </div>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            {/*<th className="ps-4" style={{ width: 40 }}>
                              <Form.Check type="checkbox" disabled />
                            </th>*/}
                            <th className="ps-4">Resident</th>
                            <th>PGY</th>
                            <th>Year</th>
                            <th>Vacation Leave</th>
                            <th>Sick Leave</th>
                            <th>Status</th>
                            <th>Requests</th>
                            <th className="text-end pe-4"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedAllotments.map(allotment => {
                            const user = allotment.resident || {};
                            const resident = user;
                            const fullName =
                              resident.firstName && resident.lastName
                                ? `${resident.firstName} ${resident.lastName}`
                                : 'Unknown Resident';
                            const initials =
                              resident.firstName || resident.lastName
                                ? `${(resident.firstName || '')
                                    .charAt(0)}${(resident.lastName || '')
                                    .charAt(0)}`.toUpperCase()
                                : 'NA';
                            const email = user.email || '';
                            const academicYearLabel = `${moment(
                              allotment.yearStart
                            ).format('YYYY')}-${moment(allotment.yearEnd).format(
                              'YYYY'
                            )}`;

                            const vacationTotal = allotment.vacationDays || 0;
                            const vacationUsed =
                              allotment.usedVacationDays || 0;
                            const vacationRemaining = Math.max(
                              vacationTotal - vacationUsed,
                              0
                            );
                            const vacationPercent =
                              vacationTotal > 0
                                ? (vacationUsed / vacationTotal) * 100
                                : 0;

                            const sickTotal = allotment.sickDays || 0;
                            const sickUsed = allotment.usedSickDays || 0;
                            const sickRemaining = Math.max(
                              sickTotal - sickUsed,
                              0
                            );
                            const sickPercent =
                              sickTotal > 0
                                ? (sickUsed / sickTotal) * 100
                                : 0;

                            const isDepleted =
                              vacationRemaining <= 0 || sickRemaining <= 0;
                            const isLowBalance =
                              !isDepleted &&
                              vacationRemaining <= LOW_BALANCE_THRESHOLD;
                            const statusLabel = isDepleted
                              ? 'Depleted'
                              : isLowBalance
                              ? 'Low'
                              : 'Good';
                            const statusVariant =
                              statusLabel === 'Depleted'
                                ? 'danger'
                                : statusLabel === 'Low'
                                ? 'warning'
                                : 'success';

                            const pendingForResident =
                              pendingRequestsByResident[
                                allotment.residentId
                              ] || 0;

                            const handleRowClick = () => {
                              const residentName = fullName;
                              showVacationHistory(
                                allotment.residentId,
                                residentName,
                                allotment
                              );
                            };

                            const getPGYBadgeColor = (pgy) => {
                              const colors = {
                                1: 'primary',
                                2: 'success',
                                3: 'warning',
                                4: 'info',
                                5: 'secondary',
                                6: 'dark',
                                7: 'danger'
                              };
                              return colors[pgy] || 'light';
                            };

                            return (
                              <tr
                                key={allotment.id}
                                className="resident-leave-row"
                                style={{ cursor: 'pointer' }}
                                onClick={handleRowClick}
                              >
                                {/*<td className="ps-4">
                                  <Form.Check type="checkbox" />
                                </td>*/}
                                <td className="ps-4">

                                  <div className="d-flex align-items-center gap-2">
                                    <UserAvatar user={resident} size={32} alt="" />
                                    <div className="d-flex flex-column">
                                    <span>{fullName}</span>
                                      <span className="text-muted small">
                                        {email || 'No email on file'}
                                      </span>
                                      </div>
                                    </div>
                                </td>
                                <td>
                                  <span className={`badge bg-${getPGYBadgeColor(resident.year)}`}>
                                    PGY-{resident.year}
                                  </span>
                                </td>
                                <td>{academicYearLabel}</td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <div className="d-flex justify-content-between small">
                                      <span>
                                        {vacationUsed} / {vacationTotal}
                                      </span>
                                      <span className="text-muted">
                                        {vacationRemaining} left
                                      </span>
                                    </div>
                                    <div
                                      className="mt-1 rounded"
                                      style={{
                                        height: 8,
                                        backgroundColor: 'var(--gray-300)',
                                        minWidth: 80
                                      }}
                                    >
                                      <div
                                        className="rounded"
                                        style={{
                                          height: '100%',
                                          width: `${Math.min(100, Math.max(0, vacationPercent))}%`,
                                          minWidth: vacationTotal > 0 ? 4 : 0,
                                          backgroundColor: 'var(--blue)',
                                          transition: 'width 0.2s ease'
                                        }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <div className="d-flex justify-content-between small">
                                      <span>
                                        {sickUsed} / {sickTotal}
                                      </span>
                                      <span className="text-muted">
                                        {sickRemaining} left
                                      </span>
                                    </div>
                                    <div
                                      className="mt-1 rounded"
                                      style={{
                                        height: 8,
                                        backgroundColor: 'var(--gray-300)',
                                        minWidth: 80
                                      }}
                                    >
                                      <div
                                        className="rounded"
                                        style={{
                                          height: '100%',
                                          width: `${Math.min(100, Math.max(0, sickPercent))}%`,
                                          minWidth: sickTotal > 0 ? 4 : 0,
                                          backgroundColor: 'var(--indigo)',
                                          transition: 'width 0.2s ease'
                                        }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <Badge bg={statusVariant} className="rounded-pill">
                                    {statusLabel}
                                  </Badge>
                                </td>
                                <td>
                                  {pendingForResident > 0 ? (
                                    <span className="badge rounded-pill bg-warning text-dark">
                                      {pendingForResident} pending
                                    </span>
                                  ) : (
                                    <span className="text-muted small">None</span>
                                  )}
                                </td>
                                <td className="text-end pe-4">
                                  <Button
                                    variant="light-lite"
                                    size="sm"
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleRowClick();
                                    }}
                                  >
                                    <i className="bi bi-three-dots-vertical"></i>
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>

                
                </Card>
              )}
                   {totalLeaveResidents > 0 && allotmentTotalPages > 1 && (
                      <Pagination
                        currentPage={currentAllotmentPage}
                        totalPages={allotmentTotalPages}
                        onPageChange={handleAllotmentPageChange}
                      />
                  )} 
            </Col>

          </Row>
        </Tab.Pane>
        </Tab.Content>
      </Container>

      {/* Vacation History Modal */}
      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="lg">
        <Modal.Header closeButton className="border-bottom-0">
          <Modal.Title className="d-flex flex-column justify-content-start align-items-start w-100">
            <span>Leave History - {selectedResident?.name || 'Resident'}</span>
            <div className="text-hl"></div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-0">
          {selectedResidentAllotment && (
            <div className="px-4 pt-0 pb-4">
              <Row className="g-3">
                <Col md={6}>
                  <Card className="border-0 bg-light h-100">
                    <Card.Header className="border-0 px-3 pt-3 bg-light">
                      <h6 className="mb-0 fw-semibold text-muted">Vacation Leave</h6>
                    </Card.Header>
                    <Card.Body className="px-3 pt-0 pb-3 d-flex flex-row align-items-center justify-content-center gap-3">
                      <div className="d-flex flex-column align-items-center" style={{ minWidth: 120 }}>
                        <div style={{ width: 120, height: 60, position: 'relative' }}>
                          <svg width="120" height="60" viewBox="0 0 120 60">
                            <path
                              d="M 20 50 A 40 40 0 0 1 100 50"
                              fill="none"
                              stroke="var(--gray-300, #dee2e6)"
                              strokeWidth="8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M 20 50 A 40 40 0 0 1 100 50"
                              fill="none"
                              stroke="var(--blue, #0d6efd)"
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${((selectedResidentAllotment.usedVacationDays || 0) / (selectedResidentAllotment.vacationDays || 1)) * 125.66} 125.66`}
                            />
                          </svg>
                          <div style={{ position: 'absolute', top: '65%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <div style={{ fontSize: 14, fontWeight: 'bold', color: 'var(--blue, #0d6efd)' }}>
                              {selectedResidentAllotment.usedVacationDays ?? 0}
                            </div>
                            <div style={{ fontSize: 10, color: '#6c757d' }}>
                              of {selectedResidentAllotment.vacationDays ?? 0}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="small text-muted">Used</span>
                          <span className="small fw-medium">{selectedResidentAllotment.usedVacationDays ?? 0} days</span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="small text-muted">Remaining</span>
                          <span className="small fw-medium text-success">
                            {Math.max((selectedResidentAllotment.vacationDays ?? 0) - (selectedResidentAllotment.usedVacationDays ?? 0), 0)} days
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card className="border-0 bg-light h-100">
                    <Card.Header className="border-0 px-3 pt-3 bg-light">
                      <h6 className="mb-0 fw-semibold text-muted">Sick Leave</h6>
                    </Card.Header>
                    <Card.Body className="px-3 pt-0 pb-3 d-flex flex-row align-items-center justify-content-center gap-3">
                      <div className="d-flex flex-column align-items-center" style={{ minWidth: 120 }}>
                        <div style={{ width: 120, height: 60, position: 'relative' }}>
                          <svg width="120" height="60" viewBox="0 0 120 60">
                            <path
                              d="M 20 50 A 40 40 0 0 1 100 50"
                              fill="none"
                              stroke="var(--gray-300, #dee2e6)"
                              strokeWidth="8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M 20 50 A 40 40 0 0 1 100 50"
                              fill="none"
                              stroke="var(--indigo, #6610f2)"
                              strokeWidth="8"
                              strokeLinecap="round"
                              strokeDasharray={`${((selectedResidentAllotment.usedSickDays || 0) / (selectedResidentAllotment.sickDays || 1)) * 125.66} 125.66`}
                            />
                          </svg>
                          <div style={{ position: 'absolute', top: '65%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <div style={{ fontSize: 14, fontWeight: 'bold', color: 'var(--indigo, #6610f2)' }}>
                              {selectedResidentAllotment.usedSickDays ?? 0}
                            </div>
                            <div style={{ fontSize: 10, color: '#6c757d' }}>
                              of {selectedResidentAllotment.sickDays ?? 0}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="small text-muted">Used</span>
                          <span className="small fw-medium">{selectedResidentAllotment.usedSickDays ?? 0} days</span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="small text-muted">Remaining</span>
                          <span className="small fw-medium text-success">
                            {Math.max((selectedResidentAllotment.sickDays ?? 0) - (selectedResidentAllotment.usedSickDays ?? 0), 0)} days
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
          {loadingVacationRequests ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" className="mb-3 text-primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-muted">Loading leave requests...</p>
            </div>
          ) : residentVacationRequests.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-calendar-x fs-1 mb-3 d-block text-muted"></i>
              <p className="text-muted">No approved leave requests found for the current academic year.</p>
              <small className="text-muted">
                Academic Year: {filterAcademicYear}-{parseInt(filterAcademicYear) + 1}
              </small>
            </div>
          ) : (
            <div>
              <div className="mb-3 px-4">
                <h6 className="text-muted mb-0">
                  Approved Leave Requests - Academic Year: {filterAcademicYear}-{parseInt(filterAcademicYear) + 1}
                </h6>
                <small className="text-muted">
                  Total approved requests: {residentVacationRequests.length}
                </small>
              </div>
              
              <div className="table-responsive border-top border-bottom" style={{ borderRadius: '0' }}>
                <Table hover className="mb-0">
                  <thead>
                    <tr className="custom-header-bg">
                      <th className="ps-4">Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {residentVacationRequests.map((request, index) => (
                      <tr key={request.id || index}>
                        <td className="ps-4">
                            {request.type === 'Sick' ? 'Sick Leave' : request.type}

                        </td>
                        <td>{moment(request.startDate).format('MMM DD, YYYY')}</td>
                        <td>{moment(request.endDate).format('MMM DD, YYYY')}</td>
                        <td>
                          <span className="fw-medium">
                            {moment(request.endDate).diff(moment(request.startDate), 'days') + 1}
                          </span>
                        </td>
                        <td>
                          <Badge 
                            bg={request.status === 'Approved' ? 'success' : 
                                request.status === 'Pending' ? 'warning' : 'danger'}
                            className="rounded-pill"
                          >
                            {request.status}
                          </Badge>
                        </td>
                        <td>
                          <span className="text-muted small">
                            {request.reason || 'No reason provided'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button variant="secondary" size="sm" onClick={handleCloseHistoryModal}>
            Close
          </Button>
          {(residentVacationRequests.length > 0 || selectedResidentAllotment) && (
            <Button variant="outline-primary" size="sm" onClick={handlePrintLeaveHistory}>
              <i className="bi bi-printer me-2"></i>
              Print Summary
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showReportModal} onHide={handleCloseReportModal}>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title> <div className="d-flex flex-column justify-content-start align-items-start">
          <span>Generate Work Log Report</span>
          <div className="text-hl">
          </div>
        </div>
      </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleReportSubmit}>
        <Modal.Body className="px-4">
          <Row>
          <Col md={12} className="mb-3">
            <FloatingLabel controlId="floatingInput" label="Resident" className="mb-3">
            <Form.Control type="text" value={reportResident ? `${reportResident.firstName} ${reportResident.lastName}` : ''} readOnly />
            </FloatingLabel>
          </Col>
          <Col md={6} className="mb-3">
            <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
            <Form.Control type="date" value={reportStartDate} onChange={e => setReportStartDate(e.target.value)} required />
            </FloatingLabel>
          </Col>
          <Col md={6} className="mb-3">
            <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
            <Form.Control type="date" value={reportEndDate} onChange={e => setReportEndDate(e.target.value)} required />
            </FloatingLabel>
          </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button variant="light" size="sm" onClick={handleCloseReportModal}>Cancel</Button>
          <Button variant="primary" size="sm" type="submit">Generate</Button>
        </Modal.Footer>
      </Form>
    </Modal>
    <Modal show={showUnlockConfirm} onHide={handleCloseUnlockConfirm}>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
          <span>Unlock Submission</span>
          <div className="text-hl"></div>
        </div>
      </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        Are you sure you want to unlock the most recent submitted work hour log for {unlockResident ? `${unlockResident.firstName} ${unlockResident.lastName}` : ''}?<br/>
        <br/>This will allow the resident to edit their work hours for that submission.
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" onClick={handleCloseUnlockConfirm}>Cancel</Button>
        <Button variant="danger" onClick={handleUnlockSubmission}>Unlock</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showApproveModal} onHide={() => { setShowApproveModal(false); setSelectedRequest(null); }}>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
          <span>Review Leave Request</span>
          <div className="text-hl"></div>
        </div>
      </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <div className="mb-3">
          <p><strong>Resident:</strong> {selectedRequest?.resident ? `${selectedRequest.resident.firstName} ${selectedRequest.resident.lastName}` : ''}</p>
          <p><strong>Type:</strong> {selectedRequest?.type || ''}</p>
          <p><strong>Date Range:</strong> {selectedRequest ? `${moment(selectedRequest.startDate).format('YYYY-MM-DD')} to ${moment(selectedRequest.endDate).format('YYYY-MM-DD')}` : ''}</p>
          <p><strong>Requested:</strong> {selectedRequest ? moment(selectedRequest.createdAt).format('YYYY-MM-DD') : ''}</p>
          {selectedRequest?.reason && (
            <p><strong>Reason:</strong> {selectedRequest.reason}</p>
          )}
        </div>
        <p className="mb-0">What would you like to do with this request?</p>
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" size="sm" onClick={() => { setShowApproveModal(false); setSelectedRequest(null); }}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          size="sm" 
          onClick={() => {
            setShowApproveModal(false);
            setShowDenyModal(true);
          }}
          disabled={actionLoading}
        >
          Deny
        </Button>
        <Button variant="success" size="sm" onClick={handleApproveRequest} disabled={actionLoading}>
          {actionLoading ? <Spinner animation="border" size="sm" /> : 'Approve'}
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showDenyModal} onHide={() => { setShowDenyModal(false); setSelectedRequest(null); setDenyReason(''); }}>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50"> <div className="d-flex flex-column justify-content-start align-items-start">
          <span>Deny Request</span>
          <div className="text-hl"></div>
        </div>
      </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <p>Are you sure you want to deny this {selectedRequest?.type?.toLowerCase()} request?</p>
        <p>
          <strong>Resident:</strong> {selectedRequest?.resident ? `${selectedRequest.resident.firstName} ${selectedRequest.resident.lastName}` : ''}
          <br />
          <strong>Date Range:</strong> {selectedRequest ? `${moment(selectedRequest.startDate).format('YYYY-MM-DD')} to ${moment(selectedRequest.endDate).format('YYYY-MM-DD')}` : ''}
        </p>
        <Form.Group>
          <FloatingLabel controlId="floatingInput" label="Reason for Denial">
          <Form.Control
            as="textarea"
            rows={3}
            value={denyReason}
            onChange={e => setDenyReason(e.target.value)}
            placeholder="Please provide a reason for denying this request"
            required
          />
          </FloatingLabel>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" size="sm" onClick={() => { setShowDenyModal(false); setSelectedRequest(null); setDenyReason(''); }}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          size="sm"
          onClick={handleDenyRequest} 
          disabled={actionLoading || !denyReason.trim()}
        >
          {actionLoading ? <Spinner animation="border" size="sm" /> : 'Deny'}
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showSettingsModal} onHide={closeSettingsModal}>
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title className="w-50"> 
          <div className="d-flex flex-column justify-content-start align-items-start">
          <span>Leave Settings</span>
          <div className="text-hl"></div>
        </div>
      </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <div className="alert alert-info border-0">
          Choose when the new leave days should take effect. The change will apply starting <strong>July 1</strong> of the selected academic year, and will update that academic year and all future academic years.
        </div>
        {settingsLoading ? <Spinner animation="border" /> : (
          <Form>
            <Row>
              <Col md={6} className="mb-2">
                <Form.Group>
                  <FloatingLabel controlId="effectiveAcademicYear" label="Effective Academic Year Start (July 1)">
                    {(() => {
                      const now = moment();
                      const currentAcademicYearStart = now.month() >= 6 ? now.year() : now.year() - 1;
                      const nextAcademicYearStart = currentAcademicYearStart + 1;
                      return (
                        <Form.Select
                          value={settingsEffectiveAcademicYear}
                          onChange={(e) => setSettingsEffectiveAcademicYear(e.target.value)}
                        >
                          <option value="current">
                            Current ({currentAcademicYearStart}-{currentAcademicYearStart + 1})
                          </option>
                          <option value="next">
                            Next ({nextAcademicYearStart}-{nextAcademicYearStart + 1})
                          </option>
                        </Form.Select>
                      );
                    })()}
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Allotted Vacation Days">
              <Form.Control type="number" name="vacationDays" value={settings.vacationDays} min={0} onChange={handleSettingsChange} />
              </FloatingLabel>
            </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Allotted Sick Days">
              <Form.Control type="number" name="sickDays" value={settings.sickDays} min={0} onChange={handleSettingsChange} />
              </FloatingLabel>
            </Form.Group>
              </Col>
              <Col md={6} className="mb-2">
            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Academic Year Start Month">
              <Form.Select name="academicYearStartMonth" value={settings.academicYearStartMonth} onChange={handleSettingsChange}>
                {[
                  'January','February','March','April','May','June','July','August','September','October','November','December'
                ].map((m, i) => <option key={i} value={i}>{m}</option>)}
              </Form.Select>
              </FloatingLabel>
            </Form.Group>
              </Col>
              <Col md={6}>
            <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Academic Year Start Day">
              <Form.Control type="number" name="academicYearStartDay" value={settings.academicYearStartDay} min={1} max={31} onChange={handleSettingsChange} />
              </FloatingLabel>
            </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" size="sm" onClick={closeSettingsModal}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSettingsSave} disabled={settingsSaving || settingsLoading}>
          {settingsSaving ? <Spinner animation="border" size="sm" /> : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  </div>
);
};

export default DirectorWorkLogDashboard;

